import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Post, HydratedPost, PostType, MediaAttachment } from '../types';
import { useAuth, useAudioPlayerState, useAudioPlayerProgress } from '../contexts/AuthContext';
import { FormattedText } from './FormattedText';
import api, { API_URL } from '../api';
import { MOD_REGISTRY, getDynamicModDefinition, getModBadgesForPost } from '../utils/ModRegistry';
import { PostActionWheel } from './PostActionWheel';
import { canViewPost, handleSliderPointerDown, getNormalizedPostcardShape } from '../utils/helpers';
import {
    LikeIcon,
    BrokenHeartIcon,
    BumpIcon,
    PrivateIcon,
    CommentIcon,
    SendIcon,
    ExternalLinkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    AudioIcon,
    MicIcon,
    VolumeOffIcon,
    VolumeHighIcon,
    EyeOffIcon,
    PlayIcon,
    GoodieWhiteIcon,
    CheckmarkIcon,
    BookmarkIcon,
    EditIcon,
    TrashIcon,
    UsersIcon,
    MailIcon,
    BellIcon
} from './Icons';

// Sub-components are actually in their own files now
import { PostActionButtonBar } from './PostActionButtonBar';
import { stickerTextStyle } from './StickerText';
import { RemoteStreamView } from './VoiceRoom';
import { STORE_ITEMS, FONT_OPTIONS } from '../constants';

const PostCardAudioSeekBar = React.memo(({
    post,
    audioCurrentTime,
    audioDuration,
    handleSeek,
    handleSliderPointerDown,
    triggerSeekbarVisibility
}: {
    post: HydratedPost;
    audioCurrentTime: number;
    audioDuration: number;
    handleSeek: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSliderPointerDown: (e: React.PointerEvent<HTMLInputElement>, value: number, min: number, max: number) => void;
    triggerSeekbarVisibility: () => void;
}) => {
    const { audioCurrentTime: globalAudioTime, audioDuration: globalAudioDuration } = useAudioPlayerProgress() || {};
    const { activeAudioPost } = useAudioPlayerState() || {};

    const isCurrentGlobalAudio = activeAudioPost?.id === post.id;
    const displayAudioTime = isCurrentGlobalAudio ? (globalAudioTime || 0) : audioCurrentTime;
    const displayAudioDuration = isCurrentGlobalAudio ? (globalAudioDuration || 0) : audioDuration;

    return (
        <div className="absolute bottom-0 left-0 right-0 p-4 z-50 transition-all duration-300 transform translate-y-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
                <input 
                    type="range" 
                    min="0" 
                    max={displayAudioDuration || 100} 
                    value={displayAudioTime} 
                    onChange={handleSeek} 
                    onPointerDown={(e) => { 
                        handleSliderPointerDown(e, displayAudioTime, 0, displayAudioDuration || 1); 
                        e.stopPropagation(); 
                        triggerSeekbarVisibility(); 
                    }} 
                    className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer accent-green-400" 
                    style={{ 
                        background: `linear-gradient(to right, #4ade80 ${(displayAudioTime / (displayAudioDuration || 1)) * 100}%, rgba(255,255,255,0.2) ${(displayAudioTime / (displayAudioDuration || 1)) * 100}%)` 
                    }} 
                />
            </div>
        </div>
    );
});

export interface PostCardProps {
    post: HydratedPost;
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number, post?: HydratedPost) => void;
    onArchive?: (postId: number) => void;
    onUnarchive?: (postId: number) => void;
    onNavigateToProfile: (userId: string) => void;
    onHoldProfile?: (userId: string) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (communityId: string, channelId?: string) => void;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    onVouch?: (postId: number) => void;
    onApprovePost?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    columns?: number;
    isModalOpen?: boolean;
    isNonMemberOfPublicCommunity?: boolean;
    onReportPost?: (postId: number) => void;
    isDemo?: boolean;
    viewerPostcardDefault?: string;
    viewerPostcardAppearance?: 'original' | 'equipped';
    closeInternalModalsTrigger?: number;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    externalGridStyle?: React.CSSProperties;
    isProfile?: boolean;
    onAddToCart?: (post: any) => void;
    isShakingMode?: boolean;
    postcardGap?: number;
    onPostDragProgress?: (post: any, progress: number, direction: 'left' | 'right' | null) => void;
    onUpdatePostcardLayout?: (postId: number, layout: 'square' | 'dynamic') => void;
    isHome?: boolean;
    onPin?: (postId: number) => void;
    onUnpin?: (postId: number) => void;
}

// Stable YouTube Embed to prevent flickering on re-renders
const StableYouTubeEmbed = React.memo(({ html }: { html: string }) => (
    <div
        className="w-full h-full flex items-center justify-center [&>iframe]:w-full [&>iframe]:h-full"
        dangerouslySetInnerHTML={{ __html: html }}
    />
));

export const TiktokEmbed = React.memo(({ videoId, apiUrl }: { videoId: string; apiUrl: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleResize = () => {
            const rect = container.getBoundingClientRect();
            if (rect.width && rect.height) {
                // Native dimensions of the TikTok embed to prevent any internal scrolling/cropping
                const nativeW = 325;
                const nativeH = 578;
                const scaleFactor = Math.min(rect.width / nativeW, rect.height / nativeH);
                setScale(scaleFactor);
            }
        };

        handleResize();
        const observer = new ResizeObserver(handleResize);
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef} className="w-full h-full relative overflow-hidden flex items-center justify-center">
            <div style={{
                width: '325px',
                height: '578px',
                transform: `scale(${scale})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
            }}>
                <iframe
                    className="w-full h-full border-none"
                    src={`${apiUrl}tiktok-proxy?videoId=${videoId}`}
                    allow="autoplay; fullscreen"
                    title="TikTok Video Player"
                    scrolling="no"
                />
            </div>
        </div>
    );
});

const InstagramEmbed = React.memo(({ url }: { url: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    const mediaId = useMemo(() => {
        const match = url.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/);
        return match ? match[1] : '';
    }, [url]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleResize = () => {
            const rect = container.getBoundingClientRect();
            if (rect.width && rect.height) {
                const nativeW = 325;
                const nativeH = 450;
                const scaleFactor = Math.min(rect.width / nativeW, rect.height / nativeH);
                setScale(scaleFactor);
            }
        };

        handleResize();
        const observer = new ResizeObserver(handleResize);
        observer.observe(container);
        return () => observer.disconnect();
    }, []);

    if (!mediaId) return null;

    return (
        <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-black flex items-center justify-center">
            <div style={{
                width: '325px',
                height: '400px',
                transform: `scale(${scale})`,
                transformOrigin: 'center center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden'
            }}>
                <iframe
                    className="w-full border-none bg-black"
                    src={`https://www.instagram.com/p/${mediaId}/embed/`}
                    allow="autoplay; encrypted-media; fullscreen"
                    title="Instagram Embed"
                    scrolling="no"
                    style={{
                        height: '510px',
                        marginTop: '-58px',
                        marginBottom: '-52px'
                    }}
                />
            </div>
        </div>
    );
});

export const FacebookEmbed = React.memo(({ url, preview }: { url: string; preview?: any }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);
    const [useIframe, setUseIframe] = useState(false);

    const isVideo = useMemo(() => {
        return url.includes('/reel/') || url.includes('/reels/') || url.includes('/watch') || url.includes('/video') || url.includes('/share/r/');
    }, [url]);

    const nativeW = 350;
    const nativeH = isVideo ? 578 : 500;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleResize = () => {
            const rect = container.getBoundingClientRect();
            if (rect.width && rect.height) {
                const scaleFactor = Math.min(rect.width / nativeW, rect.height / nativeH);
                setScale(scaleFactor);
            }
        };

        handleResize();
        const observer = new ResizeObserver(handleResize);
        observer.observe(container);
        return () => observer.disconnect();
    }, [nativeH]);

    if (useIframe) {
        if (preview?.video) {
            return (
                <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-black flex flex-col items-center justify-center">
                    <video 
                        src={preview.video} 
                        controls 
                        autoPlay 
                        className="w-full h-full object-contain" 
                        playsInline
                        onClick={(e) => e.stopPropagation()}
                    />
                    <button 
                        onClick={(e) => { e.stopPropagation(); setUseIframe(false); }}
                        className="absolute top-3 right-3 z-30 bg-black/70 hover:bg-black/90 text-white/80 hover:text-white px-2.5 py-1 rounded-full text-[10px] font-bold backdrop-blur-sm transition-all border border-white/10 flex items-center gap-1 shadow-lg pointer-events-auto"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        Show Preview
                    </button>
                </div>
            );
        }

        const embedUrl = `${API_URL}facebook-proxy?url=${encodeURIComponent(url)}`;
        return (
            <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-black flex items-center justify-center">
                <div style={{
                    width: `${nativeW}px`,
                    height: `${nativeH}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: 'center center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <iframe
                        className="w-full h-full border-none bg-black"
                        src={embedUrl}
                        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                        title="Facebook Embed"
                        scrolling="no"
                    />
                    
                    {/* Overlay to trigger the post modal when clicking on the active iframe */}
                    <div className="absolute inset-0 z-20 pointer-events-auto cursor-pointer" />

                    <button 
                        onClick={(e) => { e.stopPropagation(); setUseIframe(false); }}
                        className="absolute top-3 right-3 z-30 bg-black/70 hover:bg-black/90 text-white/80 hover:text-white px-2.5 py-1 rounded-full text-[10px] font-bold backdrop-blur-sm transition-all border border-white/10 flex items-center gap-1 shadow-lg pointer-events-auto"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        Show Preview
                    </button>
                </div>
            </div>
        );
    }

    const title = preview?.title || 'Facebook Content';
    const description = preview?.description || 'View this content on Facebook.';
    const imageUrl = preview?.image || 'https://upload.wikimedia.org/wikipedia/commons/b/b8/2021_Facebook_icon.svg';

    const getCleanFacebookTitle = (fbUrl: string, rawTitle: string) => {
        if (rawTitle && rawTitle !== 'Facebook Content' && rawTitle !== 'Facebook' && !rawTitle.toLowerCase().includes('log in') && !rawTitle.toLowerCase().includes('login')) {
            return rawTitle;
        }
        try {
            const parsed = new URL(fbUrl);
            const pathParts = parsed.pathname.split('/').filter(Boolean);
            if (pathParts.length > 0) {
                const firstPart = pathParts[0];
                if (firstPart !== 'posts' && firstPart !== 'reel' && firstPart !== 'reels' && firstPart !== 'videos' && firstPart !== 'watch' && firstPart !== 'share' && firstPart !== 'groups') {
                    return `Post by @${firstPart}`;
                }
                if (firstPart === 'groups' && pathParts.length > 1) {
                    return `Post in Group: ${pathParts[1]}`;
                }
            }
        } catch (e) {}
        return 'Facebook Post';
    };

    const cleanTitle = getCleanFacebookTitle(url, title);
    const isDefaultLogo = !preview?.image || imageUrl.includes('Facebook_icon') || imageUrl.includes('facebook_icon');

    if (isDefaultLogo) {
        // Minimalist layout for default Facebook link (no scraped content image)
        return (
            <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-gradient-to-br from-[#0c1f3d] via-[#050b1a] to-black flex flex-col justify-between p-4 group/fb">
                {/* Header Row */}
                <div className="relative z-10 flex items-center justify-between w-full">
                    <div 
                        onClick={(e) => { e.stopPropagation(); window.open(url, '_blank', 'noopener,noreferrer'); }}
                        className="flex items-center gap-2 text-left opacity-85 hover:opacity-100 cursor-pointer pointer-events-auto transition-opacity"
                        title="Open original post on Facebook"
                    >
                        <div className="w-6 h-6 rounded-full bg-[#1877F2] flex items-center justify-center shadow-md">
                            <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                        </div>
                        <span className="text-[10px] font-extrabold text-white/80 uppercase tracking-widest flex items-center gap-1">
                            Facebook
                            <svg className="w-2 h-2 opacity-50" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                            </svg>
                        </span>
                    </div>
                    
                    <button 
                        onClick={(e) => { e.stopPropagation(); setUseIframe(true); }}
                        className="bg-white/10 hover:bg-white/20 text-white text-[9px] font-bold py-1 px-2.5 rounded-full backdrop-blur-sm transition-all border border-white/15 flex items-center gap-1 shadow-md relative z-30 pointer-events-auto"
                    >
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
                        Load Embed
                    </button>
                </div>

                {/* Single, Centralized Facebook Branding (No layered copies) */}
                <div className="flex-1 w-full flex flex-col items-center justify-center p-6 text-center z-10">
                    <div className="w-16 h-14 rounded-3xl bg-[#1877F2]/10 border border-[#1877F2]/30 flex items-center justify-center mb-3 shadow-lg group-hover/fb:scale-110 transition-transform duration-500">
                        <svg className="w-8 h-8 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </div>
                    
                    {/* Clean typography without bulky wrapping borders or dark panels */}
                    <div className="max-w-[280px]">
                        <h4 className="text-[12px] font-black text-white/90 tracking-wide line-clamp-1 mb-1">
                            {cleanTitle}
                        </h4>
                        <p className="text-[9px] text-white/60 line-clamp-2 leading-relaxed">
                            {description !== 'View this content on Facebook.' ? description : 'This post can be viewed directly on Facebook.'}
                        </p>
                    </div>
                </div>

                <div className="h-2" /> {/* Small padding balance */}
            </div>
        );
    }

    // High-fidelity layout with custom scraped post content/image (No double layering)
    return (
        <div ref={containerRef} className="w-full h-full relative overflow-hidden bg-black flex flex-col justify-between p-4 group/fb">
            {/* Full-bleed crisp scraped photo/video frame directly as background (No double layered aspect frame) */}
            <div className="absolute inset-0 z-0">
                <img src={imageUrl} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover/fb:scale-105" />
                {/* Mood linear-radial vignette overlay for rich contrast */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/50" />
            </div>

            {/* Header Row */}
            <div className="relative z-10 flex items-center justify-between w-full">
                <div 
                    onClick={(e) => { e.stopPropagation(); window.open(url, '_blank', 'noopener,noreferrer'); }}
                    className="flex items-center gap-1.5 text-left opacity-90 drop-shadow-md cursor-pointer hover:opacity-100 transition-opacity pointer-events-auto"
                    title="Open original post on Facebook"
                >
                    <div className="w-5.5 h-5.5 rounded-full bg-[#1877F2] flex items-center justify-center shadow-md">
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </div>
                    <span className="text-[9px] font-extrabold text-white uppercase tracking-widest flex items-center gap-1">
                        Facebook
                        <svg className="w-2 h-2 opacity-50" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                        </svg>
                    </span>
                </div>
                
                <button 
                    onClick={(e) => { e.stopPropagation(); setUseIframe(true); }}
                    className="bg-black/40 hover:bg-black/60 text-white text-[9px] font-bold py-1 px-2.5 rounded-full backdrop-blur-md transition-all border border-white/10 flex items-center gap-1 shadow-md relative z-30 pointer-events-auto"
                >
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
                    Load Embed
                </button>
            </div>

            {/* Central Play/Media Icon Overlay */}
            <div className="relative z-10 flex-1 w-full flex items-center justify-center">
                {isVideo && (
                    <div className="w-12 h-12 rounded-full bg-[#1877F2]/90 border border-white/10 flex items-center justify-center text-white shadow-2xl backdrop-blur-xs transition-all duration-300 group-hover/fb:scale-110">
                        <svg className="w-5.5 h-5.5 ml-1 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                )}
            </div>

            {/* Clean, shadow-lifted typography at the bottom (No heavy-framed panels) */}
            <div className="relative z-10 w-full text-left drop-shadow-lg px-1 pb-1">
                <h4 className="text-[12px] font-extrabold text-white leading-tight line-clamp-1 mb-0.5">
                    {cleanTitle}
                </h4>
                <p className="text-[9px] text-white/80 line-clamp-2 leading-snug">
                    {description}
                </p>
            </div>
        </div>
    );
});

const PostCardLiveStreamPlayer: React.FC<{ streamObj: MediaStream | null }> = React.memo(({ streamObj }) => {
    const [isMuted, setIsMuted] = useState(true);
    const [aspectRatio, setAspectRatio] = useState<number | null>(() => {
        if (streamObj) {
            const track = streamObj.getVideoTracks()[0];
            const settings = track?.getSettings();
            if (settings?.width && settings?.height) {
                return settings.width / settings.height;
            }
        }
        return (16 / 9);
    });

    useEffect(() => {
        if (!streamObj) return;
        const track = streamObj.getVideoTracks()[0];
        if (!track) return;

        const updateRatio = () => {
            const settings = track.getSettings();
            if (settings?.width && settings?.height) {
                setAspectRatio(settings.width / settings.height);
            }
        };
        updateRatio();
    }, [streamObj]);

    const aspectStyle: React.CSSProperties = aspectRatio
        ? { aspectRatio: `${aspectRatio}` }
        : { aspectRatio: '16 / 9' };

    return (
        <div 
            className="relative w-full bg-black flex items-center justify-center overflow-hidden transition-all duration-300 min-h-[200px]"
            style={aspectStyle}
        >
            <RemoteStreamView
                stream={streamObj}
                muted={isMuted}
                className="w-full h-full object-cover"
                onAspectRatioChange={(ratio) => {
                    if (ratio && !isNaN(ratio)) {
                        setAspectRatio(ratio);
                    }
                }}
            />
            <div className="absolute top-3 left-3 bg-red-600/90 text-white font-bold text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-lg animate-pulse z-20">
                <span className="w-2 h-2 bg-white rounded-full animate-ping" />
                LIVE SCREEN SHARE
            </div>

            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsMuted(prev => !prev);
                }}
                className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 text-white rounded-full border border-white/20 backdrop-blur-md transition-all active:scale-90 z-20 shadow-lg flex items-center justify-center"
                title={isMuted ? "Unmute Audio" : "Mute Audio"}
            >
                {isMuted ? (
                    <VolumeOffIcon className="w-4 h-4 text-red-400" />
                ) : (
                    <VolumeHighIcon className="w-4 h-4 text-green-400" />
                )}
            </button>
        </div>
    );
});

export const PostCard = React.memo(({
    post: initialPost,
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onArchive,
    onUnarchive,
    onNavigateToProfile,
    onHoldProfile,
    onNavigateToHashtag,
    onNavigateToCommunity,
    onShare,
    onVouch,
    onApprovePost,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    columns = 4,
    isModalOpen = false,
    isNonMemberOfPublicCommunity = false,
    isDemo = false,
    onReportPost,
    viewerPostcardDefault,
    viewerPostcardAppearance,
    closeInternalModalsTrigger = 0,
    onInternalModalStateChange,
    onUpdatePostcardLayout,
    externalGridStyle,
    isHome = false,
    onPin,
    onUnpin,
    isProfile = false,
    onAddToCart,
    isShakingMode = false,
    postcardGap = 8,
    onPostDragProgress
}: PostCardProps) => {
    const isPhone = typeof window !== 'undefined' && window.innerWidth <= 1024;
    const {
        currentUser, usersData, communities,
        activeVideo, setActiveVideo,
        activeVideoRect, setActiveVideoRect,
        ignoredPipIds, setIgnoredPipIds
    } = useAuth();

    const {
        activeAudioPost, playAudioPost, seekAudioPost,
        audioIsPlaying
    } = useAudioPlayerState();
    const [showOriginal, setShowOriginal] = useState(false);

    const isSimpleBump = useMemo(() => {
        const isBump = initialPost.type === 'bump' || initialPost.type === 'repost';
        return isBump && (!initialPost.content.text || initialPost.content.text.trim() === '');
    }, [initialPost.type, initialPost.content.text]);

    const isCaptionedBump = useMemo(() => {
        const isBump = initialPost.type === 'bump' || initialPost.type === 'repost';
        return isBump && !!initialPost.originalPost && Boolean(initialPost.content?.text && initialPost.content.text.trim() !== '');
    }, [initialPost.type, initialPost.originalPost, initialPost.content?.text]);

    useEffect(() => {
        if (!isCaptionedBump) {
            setShowOriginal(false);
            return;
        }
        const interval = setInterval(() => {
            setShowOriginal(prev => !prev);
        }, 5000);
        return () => clearInterval(interval);
    }, [isCaptionedBump]);

    const post = useMemo(() => {
        const isBump = initialPost.type === 'bump' || initialPost.type === 'repost';
        return ((showOriginal || isSimpleBump) && initialPost.originalPost) ? initialPost.originalPost : initialPost;
    }, [showOriginal, isSimpleBump, initialPost]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const nextSlide = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const length = post.type === 'bump' ? (post.originalPost?.content.media?.length ?? 0) : (post.content.media?.length ?? 0);
        if (length > 0) setCurrentImageIndex(prev => (prev + 1) % length);
    }, [post]);

    const prevSlide = useCallback((e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        const length = post.type === 'bump' ? (post.originalPost?.content.media?.length ?? 0) : (post.content.media?.length ?? 0);
        if (length > 0) setCurrentImageIndex(prev => (prev - 1 + length) % length);
    }, [post]);

    const handleToggleFit = useCallback((postId: number, mediaIndex: number) => {
        const isAudioVoice = post.type === 'audio' || post.type === 'voice';
        if (isAudioVoice) {
            const currentFit = post.content.coverImageFit || 'auto';
            const nextFit = currentFit === 'auto' ? 'horizontal' : currentFit === 'horizontal' ? 'vertical' : 'auto';
            const ev = new CustomEvent('app.updatePostContent', {
                detail: {
                    postId,
                    content: { coverImageFit: nextFit }
                }
            });
            window.dispatchEvent(ev);
        } else {
            const media = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.media : post.content.media;
            if (!media || media.length === 0) return;
            const updatedMedia = media.map((item, idx) => {
                if (idx === mediaIndex) {
                    const currentFit = item.fit || 'auto';
                    const nextFit = currentFit === 'auto' ? 'horizontal' : currentFit === 'horizontal' ? 'vertical' : 'auto';
                    return { ...item, fit: nextFit };
                }
                return item;
            });
            const ev = new CustomEvent('app.updatePostContent', {
                detail: {
                    postId,
                    content: { media: updatedMedia }
                }
            });
            window.dispatchEvent(ev);
        }
    }, [post]);

    const handleToggleShape = useCallback((postId: number) => {
        let nextShape: 'square' | 'tall' | 'wide' = 'square';
        const currentShape = post.postcardShape;
        if (currentShape === 'square') {
            nextShape = 'tall';
        } else if (currentShape === 'tall') {
            nextShape = 'wide';
        } else {
            nextShape = 'square';
        }
        const ev = new CustomEvent('app.updatePostShape', {
            detail: {
                postId,
                postcardShape: nextShape
            }
        });
        window.dispatchEvent(ev);
    }, [post.postcardShape]);

    const [isHovered, setIsHovered] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isMuted, setIsMuted] = useState(true);
    const [isVideoMuted, setIsVideoMuted] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const isPiP = activeVideo?.id === post.id && !isVisible;
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isWheelVisible, setIsWheelVisible] = useState(false);
    const [isSelected, setIsSelected] = useState(false);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);

    const [isEncountered, setIsEncountered] = useState(() => {
        try {
            // Fresh start cutoff: any post created before this is automatically considered encountered
            if (initialPost.createdAt) {
                const FRESH_START_CUTOFF = new Date('2026-07-28T23:00:00Z').getTime();
                if (new Date(initialPost.createdAt).getTime() < FRESH_START_CUTOFF) {
                    return true;
                }
            } else {
                // If there is no createdAt, it's an older post
                return true;
            }

            if (currentUser && usersData[currentUser]?.encounteredPosts) {
                const encountered = usersData[currentUser].encounteredPosts!;
                if (encountered.includes(initialPost.id) || encountered.includes(initialPost.id.toString())) {
                    return true;
                }
            }
            const saved = localStorage.getItem('encountered_posts');
            if (saved) {
                const parsed = JSON.parse(saved);
                return Array.isArray(parsed) && (parsed.includes(initialPost.id) || parsed.includes(initialPost.id.toString()));
            }
        } catch (e) {
            console.warn(e);
        }
        return false;
    });

    const markAsEncountered = useCallback(() => {
        setIsEncountered(true);
        try {
            const saved = localStorage.getItem('encountered_posts');
            let list = saved ? JSON.parse(saved) : [];
            if (!Array.isArray(list)) list = [];
            const strId = initialPost.id.toString();
            if (!list.includes(initialPost.id) && !list.includes(strId)) {
                list.push(initialPost.id);
                localStorage.setItem('encountered_posts', JSON.stringify(list));
            }
        } catch (e) {
            console.warn(e);
        }
        if (currentUser) {
            api.post(`/users/${currentUser}/encountered`, { postId: initialPost.id }).catch(console.error);
        }
    }, [initialPost.id, currentUser]);

    useEffect(() => {
        if (isEncountered) return;

        let hasBeenVisible = false;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        hasBeenVisible = true;
                    } else if (hasBeenVisible) {
                        markAsEncountered();
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [isEncountered, markAsEncountered]);

    useEffect(() => {
        if (isWheelVisible && closeInternalModalsTrigger > 0) {
            setIsWheelVisible(false);
        }
    }, [closeInternalModalsTrigger, isWheelVisible]);

    useEffect(() => {
        onInternalModalStateChange?.(isWheelVisible || isOptionsOpen);
    }, [isWheelVisible, isOptionsOpen, onInternalModalStateChange]);

    // Deselect on: outside tap, scroll, or another card being selected
    useEffect(() => {
        // Broadcast selection so all other cards deselect
        if (isSelected) {
            window.dispatchEvent(new CustomEvent('postcard-selected', { detail: { postId: post.id } }));
        } else {
            window.dispatchEvent(new CustomEvent('postcard-deselected', { detail: { postId: post.id } }));
        }

        // Deselect when another card is selected
        const handleOtherSelected = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            if (id !== post.id) setIsSelected(false);
        };

        // Handle programmatic selection (e.g. arrow keys)
        // Note: display-trigger calls are deferred to next tick to avoid
        // forward-reference issues with useCallback declarations below.
        const handleForceSelect = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            if (id === post.id) {
                setIsSelected(true);
                // Dispatch a secondary event that the fully-initialized effect will catch
                window.dispatchEvent(new CustomEvent('postcard-force-select-display', { detail: { postId: post.id } }));
                // Scroll into view - set programmatic scroll flag
                (window as any).lastProgrammaticScrollTime = Date.now();
                if (containerRef.current) {
                    containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            }
        };

        // Deselect on any scroll (covers both programmatic and finger scroll)
        const handleScroll = () => {
            const lastScroll = (window as any).lastProgrammaticScrollTime;
            if (lastScroll && Date.now() - lastScroll < 1200) {
                return; // Ignore scroll events during smooth programmatic scroll
            }
            setIsSelected(false);
        };

        // Deselect when the finger moves vertically (scroll gesture anywhere on page)
        let scrollTouchStartY = 0;
        const handleGlobalTouchStart = (e: TouchEvent) => {
            scrollTouchStartY = e.touches[0]?.clientY ?? 0;
        };
        const handleGlobalTouchMove = (e: TouchEvent) => {
            const dy = Math.abs((e.touches[0]?.clientY ?? 0) - scrollTouchStartY);
            if (dy > 20) setIsSelected(false); // Increased to 20px to ignore normal tap wiggles
        };

        // Deselect when tapping outside this card
        const handleOutsideTouch = (e: TouchEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsSelected(false);
            }
        };
        const handleOutsideClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsSelected(false);
            }
        };

        window.addEventListener('postcard-selected', handleOtherSelected);
        window.addEventListener('postcard-force-select', handleForceSelect);
        window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
        window.addEventListener('touchstart', handleGlobalTouchStart, { passive: true });
        window.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
        window.addEventListener('touchstart', handleOutsideTouch, { passive: true });
        window.addEventListener('mousedown', handleOutsideClick);

        return () => {
            window.removeEventListener('postcard-selected', handleOtherSelected);
            window.removeEventListener('postcard-force-select', handleForceSelect);
            window.removeEventListener('scroll', handleScroll, { capture: true });
            window.removeEventListener('touchstart', handleGlobalTouchStart);
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            window.removeEventListener('touchstart', handleOutsideTouch);
            window.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isSelected, post.id, post.type]);

    useEffect(() => {
        if (isSelected && (post.type === 'audio' || post.type === 'voice')) {
            if (activeAudioPost?.id !== post.id) {
                playAudioPost(post);
            }
        }
    }, [isSelected, post, activeAudioPost, playAudioPost]);

    const longPressTimerRef = useRef<number | null>(null);
    const lastTapTimeRef = useRef<number>(0);
    const lastTapPosRef = useRef<{ x: number, y: number }>({ x: 0, y: 0 });
    const touchStartRef = useRef<number>(0);
    const touchStartYRef = useRef<number>(0);
    const ignoreTouchRef = useRef<boolean>(false);
    const singleTapTimerRef = useRef<number | null>(null);
    const isDoubleTapRef = useRef<boolean>(false);
    const [showDoubleTapHeart, setShowDoubleTapHeart] = useState(false);
    const [showDoubleTapBrokenHeart, setShowDoubleTapBrokenHeart] = useState(false);
    const [showDoubleTapVouch, setShowDoubleTapVouch] = useState(false);
    const [dragOffset, setDragOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    // Use a ref so that post object reference changes (from parent re-renders)
    // don't re-trigger the effect and briefly reset progress to 0.
    const postRef = useRef(post);
    postRef.current = post;

    useEffect(() => {
        if (!isDragging || dragOffset === 0) {
            onPostDragProgress?.(postRef.current, 0, null);
            return;
        }
        const width = containerRef.current?.offsetWidth || window.innerWidth;
        const openThreshold = width * 0.35;
        const progress = Math.min(1.0, Math.max(0.0, Math.abs(dragOffset) / openThreshold));
        const direction = dragOffset > 0 ? 'right' : (dragOffset < 0 ? 'left' : null);
        onPostDragProgress?.(postRef.current, progress, direction);
    }, [dragOffset, isDragging, onPostDragProgress]);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [showMuteIcon, setShowMuteIcon] = useState(false);
    const muteIconTimeoutRef = useRef<number | null>(null);
    const [showCommunityBadge, setShowCommunityBadge] = useState(true);
    const communityBadgeTimeoutRef = useRef<number | null>(null);
    const [showCaptionMobile, setShowCaptionMobile] = useState(true);
    const captionTimeoutRef = useRef<number | null>(null);
    const optionsMenuRef = useRef<HTMLDivElement>(null);
    const seekbarHideTimeoutRef = useRef<number | null>(null);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const isCurrentGlobalAudio = activeAudioPost?.id === post.id;
    const [showSeekbar, setShowSeekbar] = useState(false);
    const [ytPlayer, setYtPlayer] = useState<any>(null);
    const [isYtPlaying, setIsYtPlaying] = useState(() => activeVideo?.id === post.id);
    const [isTiktokPlaying, setIsTiktokPlaying] = useState(false);
    const [isEmbedLoaded, setIsEmbedLoaded] = useState(false);
    const [mediaErrors, setMediaErrors] = useState<Record<string, boolean>>({});
    const [reloadKeys, setReloadKeys] = useState<Record<string, number>>({});
    const [interactMode, setInteractMode] = useState(false);

    const handleMediaReload = (e: React.MouseEvent, url: string) => {
        e.stopPropagation();
        setReloadKeys(prev => ({ ...prev, [url]: (prev[url] || 0) + 1 }));
        setMediaErrors(prev => ({ ...prev, [url]: false }));
    };
    const autoReloadMedia = (url: string) => {
        const retries = reloadKeys[url] || 0;
        if (retries < 3) {
            setTimeout(() => {
                setReloadKeys(prev => ({ ...prev, [url]: (prev[url] || 0) + 1 }));
                setMediaErrors(prev => ({ ...prev, [url]: false }));
            }, 1000);
            return true;
        }
        return false;
    };
    const embedLoaderTimeoutRef = useRef<any>(null);
    const ytContainerRef = useRef<any>(null);

    // Clean up embed loader timeout on unmount
    useEffect(() => {
        return () => {
            if (embedLoaderTimeoutRef.current) {
                clearTimeout(embedLoaderTimeoutRef.current);
            }
        };
    }, []);



    const ytVideoId = useMemo(() => {
        if (post.type !== 'link') return null;
        const linkPreview = post.content.linkPreview;
        if (!linkPreview) return null;

        // Only extract from YouTube domains — other embeds (Spotify, SoundCloud, etc.)
        // also use /embed/ in their URLs and would produce bogus "video IDs" otherwise.
        const domain = linkPreview.domain || '';
        const isYoutubeDomain = domain === 'youtube.com' || domain === 'youtu.be';

        // 1. Try parsing from embedHtml first (YouTube only)
        if (isYoutubeDomain && linkPreview.embedHtml) {
            const match = linkPreview.embedHtml.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
            if (match) return match[1];
        }

        // 2. Fallback: Parse directly from the URL
        const urlStr = linkPreview.url || '';
        try {
            // Match shorts: /shorts/ID
            const shortsMatch = urlStr.match(/\/shorts\/([a-zA-Z0-9_-]+)/);
            if (shortsMatch) return shortsMatch[1];

            // Match watch: v=ID or embed/ID or youtu.be/ID
            const watchMatch = urlStr.match(/(?:v=|\/embed\/|\/watch\?v=|\.be\/)([a-zA-Z0-9_-]{11})/);
            if (watchMatch) return watchMatch[1];
        } catch (e) {
            console.error("Failed to parse YouTube URL:", e);
        }

        return null;
    }, [post.type, post.content.linkPreview]);

    const tiktokVideoId = useMemo(() => {
        if (post.type === 'link' && post.content.linkPreview?.domain === 'tiktok.com') {
            const preview = post.content.linkPreview;
            const embedHtml = preview?.embedHtml || '';
            const url = preview?.url || '';
            const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;

            const match = url.match(tiktokRegex) || embedHtml.match(/data-video-id="(\d+)"/) || embedHtml.match(/videoId=(\d+)/);
            return match ? match[1] : null;
        }
        return null;
    }, [post.type, post.content.linkPreview]);

    const [resolvedTiktokVideoId, setResolvedTiktokVideoId] = useState<string | null>(null);

    useEffect(() => {
        if (tiktokVideoId) {
            setResolvedTiktokVideoId(tiktokVideoId);
        } else if (isVisible && post.type === 'link' && post.content.linkPreview?.domain === 'tiktok.com') {
            const url = post.content.linkPreview?.url || '';
            const tiktokRegex = /(?:https?:\/\/)?(?:www\.|vm\.|vt\.|v\.)?tiktok\.com\/.*\/video\/(\d+)/;

            const resolveTiktok = async () => {
                try {
                    const response = await api.post('metadata', { url });
                    if (response.data && response.data.url) {
                        const resolvedMatch = response.data.url.match(tiktokRegex);
                        if (resolvedMatch && resolvedMatch[1]) {
                            setResolvedTiktokVideoId(resolvedMatch[1]);
                        }
                    }
                } catch (e) {
                    console.error("Failed to resolve TikTok URL inside postcard:", e);
                }
            };
            resolveTiktok();
        }
    }, [isVisible, tiktokVideoId, post.type, post.content.linkPreview]);

    useEffect(() => {
        if (!ytVideoId) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'youtube-state' && event.data?.videoId === ytVideoId) {
                if (event.data.state === 1) { // 1 = PLAYING
                    setIsYtPlaying(true);
                    setIgnoredPipIds((prev: Set<number>) => {
                        if (prev.has(post.id)) {
                            const next = new Set(prev);
                            next.delete(post.id);
                            return next;
                        }
                        return prev;
                    });
                } else if (event.data.state === 2) { // 2 = PAUSED — only mark stopped if not in PiP
                    if (isVisible) setIsYtPlaying(false);
                }
            }
        };

        window.addEventListener('message', handleMessage);

        // Fallback: detect user clicking play inside iframe even if postMessage state is delayed
        const handleWindowBlur = () => {
            if (document.activeElement?.tagName === 'IFRAME') {
                if (containerRef.current && containerRef.current.contains(document.activeElement)) {
                    setIsYtPlaying(true);
                }
            }
        };
        window.addEventListener('blur', handleWindowBlur);

        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [ytVideoId, isVisible, post.id, setIgnoredPipIds]);

    useEffect(() => {
        if (!isVisible) {
            setIsTiktokPlaying(false);
            return;
        }
        if (isTiktokPlaying) return;

        const handleWindowBlur = () => {
            if (document.activeElement?.tagName === 'IFRAME') {
                if (containerRef.current && containerRef.current.contains(document.activeElement)) {
                    setIsTiktokPlaying(true);
                }
            }
        };
        window.addEventListener('blur', handleWindowBlur);
        return () => window.removeEventListener('blur', handleWindowBlur);
    }, [isVisible, isTiktokPlaying]);


    const appearanceSource = useMemo(() => {
        return (isSimpleBump && post.originalPost) ? post.originalPost : post;
    }, [isSimpleBump, post]);

    const postColorRgb = useMemo(() => {
        const currentUserData = usersData[currentUser];
        const authorData = usersData[appearanceSource.author];
        const activeThemeColor = currentUserData?.enforceThemeColor
            ? currentUserData?.appThemeColor
            : (appearanceSource.themeColor || authorData?.appThemeColor || '#000000');

        if (!activeThemeColor) return null;
        try {
            let hex = activeThemeColor.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return isNaN(r) || isNaN(g) || isNaN(b) ? null : `${r}, ${g}, ${b}`;
        } catch (e) {
            return null;
        }
    }, [appearanceSource.themeColor, appearanceSource.author, usersData, currentUser]);

    const postTextColor = useMemo(() => {
        const currentUserData = usersData[currentUser];
        const authorData = usersData[appearanceSource.author];
        if (currentUserData?.enforceTextColor) {
            return currentUserData.appTextColor || '#ffffff';
        }
        return appearanceSource.textColor || authorData?.appTextColor || '#ffffff';
    }, [appearanceSource.textColor, appearanceSource.author, currentUser, usersData]);

    const postFontFamily = useMemo(() => {
        const fontId = appearanceSource.fontId;
        if (!fontId || fontId === 'default' || fontId === 'font_default') return undefined;
        const font = FONT_OPTIONS.find(f => f.id === fontId);
        return font ? font.family : undefined;
    }, [appearanceSource.fontId]);

    const activeOpacity = useMemo(() => {
        const currentUserData = usersData[currentUser];
        const authorData = usersData[appearanceSource.author];
        return currentUserData?.enforceThemeOpacity ? postBlurOpacity : (appearanceSource.themeOpacity ?? authorData?.postBlurOpacity ?? 50);
    }, [usersData, currentUser, appearanceSource.author, postBlurOpacity, appearanceSource.themeOpacity]);

    const hexToRgb = (hex?: string | null) => {
        if (!hex) return null;
        try {
            let h = hex.replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
            return `${r}, ${g}, ${b}`;
        } catch (e) { return null; }
    };

    const bgRgba = useMemo(() => {
        return postColorRgb ? `rgba(${postColorRgb}, 0.85)` : 'rgba(0,0,0,0.6)';
    }, [postColorRgb]);

    const toggleColorRgb = useMemo(() => {
        // For bumps, the toggle target is the original post; otherwise target is initialPost (no-op)
        if (initialPost.type === 'bump' && initialPost.originalPost) {
            const target = showOriginal ? initialPost : initialPost.originalPost;
            const currentUserData = usersData[currentUser];
            const activeThemeColor = currentUserData?.enforceThemeColor
                ? currentUserData?.appThemeColor
                : (target.themeColor || '#000000');

            return hexToRgb(activeThemeColor || null);
        }
        return postColorRgb;
    }, [initialPost, showOriginal, postColorRgb, usersData, currentUser]);

    const toggleBgRgba = useMemo(() => toggleColorRgb ? `rgba(${toggleColorRgb}, 0.85)` : 'rgba(0,0,0,0.6)', [toggleColorRgb]);

    const mediaUrls = useMemo(() => {
        const p = post.type === 'bump' ? post.originalPost : post;
        return p?.content?.media?.map(m => m.url).join(',') || '';
    }, [post.type, post.originalPost?.content?.media, post.content?.media]);

    const [isMediaLoaded, setIsMediaLoaded] = useState(() => {
        const p = post.type === 'bump' ? post.originalPost : post;
        if (!p) return true;
        if (p.isArchived) return true; // Stop perpetual loading for archived posts
        if (p.type === 'text') return true;
        if (p.type === 'link' && (!p.content.linkPreview?.image || p.content.linkPreview?.embedHtml)) return true;
        if (p.type === 'audio' || p.type === 'voice') {
            return !p.content?.coverImageUrl;
        }
        if (!p.content?.media || p.content.media.length === 0) return true;

        return false;
    });

    useEffect(() => {
        const timeout = setTimeout(() => {
            setIsMediaLoaded(true);
        }, 10000); // 10s fallback
        return () => clearTimeout(timeout);
    }, [mediaUrls]);

    // Handle media load sync if post data changes (e.g. from optimistic to real)
    useEffect(() => {
        const isBump = post.type === 'bump' || post.type === 'repost';
        const p = isBump ? post.originalPost : post;
        if (!p) {
            setIsMediaLoaded(true);
            return;
        }

        if (p.isArchived || p.type === 'text' || !p.content?.media || p.content.media.length === 0) {
            setIsMediaLoaded(true);
            return;
        }

        if (p.type === 'link' && (!p.content.linkPreview?.image || p.content.linkPreview?.embedHtml)) {
            setIsMediaLoaded(true);
            return;
        }

        if (p.type === 'audio' || p.type === 'voice') {
            if (!p.content.coverImageUrl) {
                setIsMediaLoaded(true);
            } else {
                setIsMediaLoaded(false);
            }
        } else {
            setIsMediaLoaded(false);
        }
    }, [mediaUrls, post.content.coverImageUrl, post.originalPost?.content?.coverImageUrl]);

    const handleMediaLoad = useCallback(() => {
        setIsMediaLoaded(true);
    }, []);

    const isGallery = ((post.type === 'image' || post.type === 'video') && (post.content.media?.length ?? 0) > 1) || (post.type === 'bump' && (post.originalPost?.content.media?.length ?? 0) > 1);
    const firstMedia = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.media?.[0] : post.content.media?.[0];
    const firstAudio = (post.type === 'bump' || post.type === 'repost') ? (post.originalPost?.content.media?.find((m: MediaAttachment) => m.type === 'audio' || m.type === 'voice')) : post.content.media?.find((m: MediaAttachment) => m.type === 'audio' || m.type === 'voice');

    const triggerSeekbarVisibility = useCallback(() => {
        setShowSeekbar(true);
        if (seekbarHideTimeoutRef.current) window.clearTimeout(seekbarHideTimeoutRef.current);
        seekbarHideTimeoutRef.current = window.setTimeout(() => {
            setShowSeekbar(false);
        }, 3000);
    }, []);

    const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const time = Number(e.target.value);
        if (isCurrentGlobalAudio) {
            seekAudioPost(time);
        } else if (audioRef.current) {
            audioRef.current.currentTime = time;
            setAudioCurrentTime(time);
        }
        triggerSeekbarVisibility();
    }, [isCurrentGlobalAudio, seekAudioPost, triggerSeekbarVisibility]);

    const toggleMute = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        const audio = audioRef.current;
        if (audio) {
            const newMutedState = !audio.muted;
            audio.muted = newMutedState;
            setIsMuted(newMutedState);
            triggerSeekbarVisibility();
        }
    }, [triggerSeekbarVisibility]);

    const triggerMuteIconDisplay = useCallback(() => {
        setShowMuteIcon(true);
        if (muteIconTimeoutRef.current) clearTimeout(muteIconTimeoutRef.current);
        muteIconTimeoutRef.current = window.setTimeout(() => {
            setShowMuteIcon(false);
        }, 2000);
    }, []);

    const triggerCommunityBadgeDisplay = useCallback(() => {
        setShowCommunityBadge(true);
        if (communityBadgeTimeoutRef.current) clearTimeout(communityBadgeTimeoutRef.current);
        communityBadgeTimeoutRef.current = window.setTimeout(() => {
            setShowCommunityBadge(false);
        }, 2000);
    }, []);

    const triggerCaptionDisplay = useCallback(() => {
        setShowCaptionMobile(true);
        if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
        captionTimeoutRef.current = window.setTimeout(() => {
            setShowCaptionMobile(false);
        }, 2000);
    }, []);

    // Listen for programmatic force-select display triggers (arrow key navigation)
    useEffect(() => {
        const handleForceDisplay = (e: Event) => {
            const id = (e as CustomEvent).detail?.postId;
            if (id === post.id) {
                triggerMuteIconDisplay();
                triggerCommunityBadgeDisplay();
                if (post.type !== 'text') {
                    triggerCaptionDisplay();
                }
            }
        };
        window.addEventListener('postcard-force-select-display', handleForceDisplay);
        return () => window.removeEventListener('postcard-force-select-display', handleForceDisplay);
    }, [post.id, post.type, triggerMuteIconDisplay, triggerCommunityBadgeDisplay, triggerCaptionDisplay]);

    const toggleCaptionDisplay = useCallback(() => {
        setShowCaptionMobile(prev => {
            if (prev) {
                if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
                return false;
            } else {
                if (captionTimeoutRef.current) clearTimeout(captionTimeoutRef.current);
                captionTimeoutRef.current = window.setTimeout(() => {
                    setShowCaptionMobile(false);
                }, 2000);
                return true;
            }
        });
    }, []);

    const toggleVideoMute = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const video = videoRef.current;
        if (video) {
            const newMutedState = !video.muted;
            video.muted = newMutedState;
            setIsVideoMuted(newMutedState);
            triggerMuteIconDisplay();
        }
    }, [triggerMuteIconDisplay]);

    const mediaLength = (post.type === 'bump' || post.type === 'repost') ? (post.originalPost?.content.media?.length ?? 0) : (post.content.media?.length ?? 0);

    const isMultiTouchRef = useRef<boolean>(false);

    const handleTouchStart = (e: React.TouchEvent) => {
        const target = e.target as HTMLElement;
        if (e.touches.length > 1 || target.closest('button, a, input, textarea, .post-action-wheel')) {
            if (e.touches.length > 1) {
                isMultiTouchRef.current = true;
            }
            // Cancel long-press if a second finger lands or if touching interactive element
            if (longPressTimerRef.current !== null) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
            return;
        }

        // Bypassing navbar zone horizontal swipe (bottom 20% of the screen)
        const initialY = e.touches[0].clientY;
        const isNavbarZone = initialY > window.innerHeight * 0.8;
        // Don't set ignoreTouchRef since it blocks taps entirely. 
        // Instead, just bypass horizontal drag offset mapping if in navbar zone.
        ignoreTouchRef.current = false;

        isMultiTouchRef.current = false;
        const startX = e.targetTouches[0].clientX;
        const startY = e.targetTouches[0].clientY;

        setTouchStart(startX);
        touchStartRef.current = startX;
        touchStartYRef.current = startY;

        let hasMoved = false;

        const now = Date.now();
        const dist = Math.sqrt(Math.pow(startX - lastTapPosRef.current.x, 2) + Math.pow(startY - lastTapPosRef.current.y, 2));

        const rect = containerRef.current?.getBoundingClientRect();
        const containerWidth = rect?.width || (typeof window !== 'undefined' ? window.innerWidth : 300);
        const relativeX = startX - (rect?.left || 0);
        const isGalleryNavArea = isGallery && (relativeX < containerWidth * 0.3 || relativeX > containerWidth * 0.7);

        if (now - lastTapTimeRef.current < 300 && dist < 40 && !isGalleryNavArea) {
            e.stopPropagation();
            if (singleTapTimerRef.current !== null) {
                clearTimeout(singleTapTimerRef.current);
                singleTapTimerRef.current = null;
            }
            isDoubleTapRef.current = true;
            if (post.footprintDetails) {
                const isVouched = post.vouches?.some(v => v.userId === currentUser);
                if (onVouch) {
                    setShowDoubleTapVouch(true);
                    setTimeout(() => setShowDoubleTapVouch(false), 600);
                    if (!isVouched) {
                        onVouch(post.id);
                    }
                }
            } else {
                setShowDoubleTapHeart(true);
                setTimeout(() => setShowDoubleTapHeart(false), 600);
                if (!post.isLiked) {
                    onLike(post.id);
                }
            }
            lastTapTimeRef.current = 0;
            if (longPressTimerRef.current !== null) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
        } else {
            lastTapTimeRef.current = now;
            lastTapPosRef.current = { x: startX, y: startY };

            // Long-press to show action sheet (500ms, matching iOS message hold)
            // Long-press handler
            if (!isWheelVisible) {
                longPressTimerRef.current = window.setTimeout(() => {
                    longPressTimerRef.current = null;
                    const isPinned = usersData[initialPost.author]?.pinnedPosts?.includes(post.id);
                    if (isProfile && isPinned && isSelected) {
                        // Dispatch custom event to trigger drag and drop reordering
                        window.dispatchEvent(new CustomEvent('pinned-post-drag-start', {
                            detail: {
                                postId: post.id,
                                startX,
                                startY
                            }
                        }));
                    } else {
                        setIsWheelVisible(true);
                    }
                }, 500);
            }
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (ignoreTouchRef.current) return;
        if (!isPhone || isWheelVisible || e.touches.length > 1) {
            if (e.touches.length > 1) {
                isMultiTouchRef.current = true;
            }
            if (isDragging) {
                setIsDragging(false);
                setDragOffset(0);
            }
            // Cancel long-press on multi-touch (pinch-to-zoom)
            if (e.touches.length > 1 && longPressTimerRef.current !== null) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
            return;
        }
        const currentX = e.targetTouches[0].clientX;
        const currentY = e.targetTouches[0].clientY;
        const diffX = currentX - touchStartRef.current;
        const diffY = currentY - touchStartYRef.current;

        if (Math.abs(diffX) > 10 || Math.abs(diffY) > 10) {
            lastTapTimeRef.current = 0;
            // Cancel long-press if finger moved, unless we are preparing to drag a selected pinned post on profile
            const isPinned = usersData[initialPost.author]?.pinnedPosts?.includes(post.id);
            const isDraggingPinned = isProfile && isPinned && isSelected;
            if (longPressTimerRef.current !== null && !isDraggingPinned) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
            // Deselect on scroll (vertical movement is dominant)
            if (Math.abs(diffY) > 8 && Math.abs(diffY) > Math.abs(diffX) && isSelected) {
                setIsSelected(false);
            }
        }

        if (!isDragging) {
            // Allow dragging even if not selected ONLY if they drag right (diffX > 50) to open the post modal (Homepage only)
            const isDraggingRightToOpen = isHome && diffX > 50 && Math.abs(diffY) < Math.abs(diffX) * 0.8;
            if (!isSelected && !isDraggingRightToOpen) return;
            // Ignore if movement is primarily vertical (scrolling) 
            // or if it hasn't passed the threshold (deadzone)
            if (Math.abs(diffY) > Math.abs(diffX) * 0.8 || Math.abs(diffX) < 50) {
                return;
            }
            if (longPressTimerRef.current !== null) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
            setIsDragging(true);
        }

        // Lock the gesture to the card once dragging starts (unless in navbar zone)
        const initialY = touchStartYRef.current;
        const isNavbarZone = initialY > window.innerHeight * 0.8;
        if (isDragging && !isNavbarZone) {
            const isDraggingRightToOpen = isHome && diffX > 0;
            if (Math.abs(diffX) > 30 && !isSelected && !isDraggingRightToOpen) {
                setDragOffset(0);
                setIsDragging(false);
            } else {
                e.stopPropagation();
                if (e.cancelable) e.preventDefault();
            }
        }

        const width = containerRef.current?.offsetWidth || 300;
        const maxDrag = width / 2;

        // Provide resistance/damping: scale the movement after the deadzone
        const movement = diffX - (diffX > 0 ? 25 : -25);
        const resistantDiff = movement * 0.75;

        const clampedDiff = Math.max(-maxDrag, Math.min(maxDrag, resistantDiff));
        setDragOffset(isNavbarZone ? 0 : clampedDiff);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (ignoreTouchRef.current) {
            ignoreTouchRef.current = false;
            return;
        }

        const diffX = e.changedTouches[0].clientX - touchStartRef.current;
        const diffY = e.changedTouches[0].clientY - touchStartYRef.current;
        const isSignificantMovement = Math.abs(diffX) > 10 || Math.abs(diffY) > 10;

        // Allow bubbling to App.tsx for page swipe if we're not selected and not dragging right to open
        if (Math.abs(diffX) > 10 && !isSelected && diffX <= 0) {
            // Allow bubbling to App.tsx to handle page transitions or snapback
        } else {
            e.stopPropagation();
        }

        if (isMultiTouchRef.current) {
            setIsDragging(false);
            setDragOffset(0);
            isMultiTouchRef.current = false;
            return;
        }

        if ((e.target as HTMLElement).closest('button, a, input, textarea, .post-action-wheel')) {
            setIsDragging(false);
            setDragOffset(0);
            return;
        }

        const touchEndX = e.changedTouches[0].clientX;
        setTouchEnd(touchEndX);
        if (longPressTimerRef.current !== null) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }

        const width = containerRef.current?.offsetWidth || window.innerWidth;
        const imageThreshold = width * 0.15;
        const openThreshold = width * 0.35;

        // Check if dragging right to open (works even if not selected, Homepage only)
        const isDragRightToOpen = isHome && dragOffset > imageThreshold;

        if (isPhone && isGallery) {
            // Large swipe opens post/share (match non-gallery behavior)
            if (dragOffset > openThreshold || (isDragRightToOpen && !isSelected)) {
                handlePostClickWithMediaStop(e as any);
            } else if (dragOffset < -openThreshold && isSelected) {
                if (onShare) onShare(post);
            } else if (dragOffset > imageThreshold && isSelected) {
                setCurrentImageIndex(prev => (prev - 1 + mediaLength) % mediaLength);
            } else if (dragOffset < -imageThreshold && isSelected) {
                setCurrentImageIndex(prev => (prev + 1) % mediaLength);
            } else if (!isSignificantMovement) {
                // Treat as a tap: left 30% => prev, right 30% => next, center => open
                const rect = containerRef.current?.getBoundingClientRect();
                const left = rect?.left || 0;
                const relativeX = touchEndX - left;
                if (relativeX < width * 0.3) {
                    setCurrentImageIndex(prev => (prev - 1 + mediaLength) % mediaLength);
                } else if (relativeX > width * 0.7) {
                    setCurrentImageIndex(prev => (prev + 1) % mediaLength);
                } else {
                    if (!isDragging) {
                        if (isSelected) {
                            singleTapTimerRef.current = window.setTimeout(() => {
                                handlePostClickWithMediaStop(e as any);
                                singleTapTimerRef.current = null;
                            }, 300);
                        } else {
                            setIsSelected(true);
                            markAsEncountered();
                        }
                    }
                    triggerMuteIconDisplay();
                    triggerCommunityBadgeDisplay();
                    if (post.type !== 'text') {
                        toggleCaptionDisplay();
                    }
                }
            }
            setDragOffset(0);
        } else if (isPhone && !isWheelVisible) {
            if (isDoubleTapRef.current) {
                isDoubleTapRef.current = false;
                setDragOffset(0);
                setIsDragging(false);
                return;
            }
            if (isDragRightToOpen || (dragOffset > imageThreshold && isSelected)) {
                handlePostClickWithMediaStop(e as any);
            } else if (dragOffset < -imageThreshold && isSelected) {
                if (onShare) onShare(post);
            } else if (!isSignificantMovement) {
                // Single tap: toggle selection or open post if already selected
                if (!isDragging) {
                    if (isSelected) {
                        singleTapTimerRef.current = window.setTimeout(() => {
                            handlePostClickWithMediaStop(e as any);
                            singleTapTimerRef.current = null;
                        }, 300);
                    } else {
                        setIsSelected(true);
                        markAsEncountered();
                        const isAudioOrVoice = post.type === 'audio' || post.type === 'voice' || ((post.type === 'bump' || post.type === 'repost') && (post.originalPost?.type === 'audio' || post.originalPost?.type === 'voice'));
                        if (isAudioOrVoice) {
                            setIsMuted(false); // Unmute on selection so it plays on mobile
                        }
                    }
                }
                triggerMuteIconDisplay();
                triggerCommunityBadgeDisplay();
                if (post.type !== 'text') {
                    triggerCaptionDisplay();
                }
            }
        }

        setIsDragging(false);
        if (!isGallery) setDragOffset(0);
        onPostDragProgress?.(postRef.current, 0, null);

        // (Long-press is the trigger — no single-tap-center action needed)
    };

    const handleTouchCancel = (e: React.TouchEvent) => {
        if (longPressTimerRef.current !== null) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        setIsDragging(false);
        setDragOffset(0);
    };

    const handleCloseWheel = (e: React.TouchEvent | React.MouseEvent) => {
        e.stopPropagation();
        setIsWheelVisible(false);
    };

    // PostActionWheel manages its own dismissal via backdrop click.
    // (A capture-phase window listener here would intercept portal button clicks
    //  before they fire, since the portal DOM is outside containerRef.)

    useEffect(() => {
        const audio = audioRef.current;
        const video = videoRef.current;
        if (post.type === 'link') return;
        if (isModalOpen) {
            if (audio) { audio.pause(); audio.currentTime = 0; }
            if (video) { video.pause(); }
            return;
        }
        if (!isPhone) {
            if (isHovered) {
                if (audio && (post.type === 'audio' || post.type === 'voice' || (post.type === 'bump' && (post.originalPost?.type === 'audio' || post.originalPost?.type === 'voice')))) {
                    audio.play().catch(e => console.error("Audio preview play failed:", e));
                    audio.muted = false; // Always unmute on desktop hover
                }
                if (video) { video.play().catch(e => console.error("Video preview play failed:", e)); }
            } else {
                if (audio) { audio.pause(); audio.currentTime = 0; }
                if (video) { video.pause(); video.currentTime = 0; }
            }
        }
    }, [isHovered, post.type, isModalOpen, isPhone, isMuted]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isModalOpen) {
                        setIsVisible(true);
                        triggerCommunityBadgeDisplay();
                        if (post.type !== 'text') {
                            triggerCaptionDisplay();
                        }
                        if (videoRef.current && window.innerWidth < 1024) {
                            videoRef.current.play().catch(e => console.error("Video autoplay failed:", e));
                        }
                    } else {
                        setIsVisible(false);
                        if (videoRef.current) { videoRef.current.pause(); }
                    }
                });
            },
            { rootMargin: '600px 0px', threshold: 0.01 }
        );
        if (containerRef.current) observer.observe(containerRef.current);
        return () => { if (containerRef.current) observer.unobserve(containerRef.current); };
    }, [post.type, isModalOpen, currentImageIndex]);

    // Hand the video off to the root-level shared iframe immediately on start
    useEffect(() => {
        if (isYtPlaying && ytVideoId && (!activeVideo || activeVideo.id !== post.id) && !isModalOpen && !ignoredPipIds.has(post.id)) {
            const isShorts = post.content.linkPreview?.url?.includes('/shorts/') || post.content.linkPreview?.isYouTubeShort || false;
            setActiveVideo({
                id: post.id,
                videoId: ytVideoId,
                html: post.content.linkPreview!.embedHtml!,
                isShorts
            });
        }
    }, [isYtPlaying, ytVideoId, activeVideo, post.id, post.content.linkPreview, setActiveVideo, isModalOpen, ignoredPipIds]);

    // When user closes PiP or pauses, clear the shared iframe
    useEffect(() => {
        if (!isYtPlaying) {
            setInteractMode(false);
            if (activeVideo?.id === post.id) {
                setActiveVideo(null);
                setActiveVideoRect(null);
            }
        }
    }, [isYtPlaying, activeVideo, post.id, setActiveVideo, setActiveVideoRect]);

    const isPiPActive = activeVideoRect?.isPiP;

    // Keep activeVideoRect in sync with the postcard container element's position on scroll/resize
    useEffect(() => {
        if (!isYtPlaying || activeVideo?.id !== post.id || !containerRef.current || isPiPActive) return;

        const updateRect = () => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setActiveVideoRect((prev) => {
                if (prev && !prev.isPiP && prev.top === rect.top && prev.left === rect.left && prev.width === rect.width && prev.height === rect.height) {
                    return prev;
                }
                return {
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    isPiP: false
                };
            });
        };

        // Update initially
        updateRect();

        // Listen for scroll and resize events globally
        window.addEventListener('scroll', updateRect, { passive: true });
        window.addEventListener('resize', updateRect, { passive: true });

        return () => {
            window.removeEventListener('scroll', updateRect);
            window.removeEventListener('resize', updateRect);
        };
    }, [isYtPlaying, activeVideo, isPiPActive, post.id, setActiveVideoRect]);

    // Switch to PIP mode when scrolled out of view
    useEffect(() => {
        if (isYtPlaying && activeVideo?.id === post.id && !isVisible && !isPiPActive) {
            setActiveVideoRect((prev) => {
                if (prev?.isPiP) return prev;
                return {
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                    isPiP: true
                };
            });
        }
    }, [isYtPlaying, activeVideo, isVisible, isPiPActive, post.id, setActiveVideoRect]);

    // Cleanup local playing state if PiP is closed by the user or taken over by another video
    useEffect(() => {
        if (isYtPlaying) {
            if (activeVideo && activeVideo.id !== post.id) {
                // Another video took over
                setIsYtPlaying(false);
            } else if (!activeVideo && ignoredPipIds.has(post.id)) {
                // User explicitly closed this video's PiP
                setIsYtPlaying(false);
            }
        }
    }, [isYtPlaying, activeVideo, ignoredPipIds, post.id]);

    // Ensure only the currently focused media plays (pause others) — fixes multiple videos playing simultaneously
    useEffect(() => {
        if (!containerRef.current) return;
        const videos: HTMLVideoElement[] = Array.from(containerRef.current.querySelectorAll('video')) as HTMLVideoElement[];
        const audios: HTMLAudioElement[] = Array.from(containerRef.current.querySelectorAll('audio')) as HTMLAudioElement[];

        videos.forEach((v, idx) => {
            try {
                if (idx === currentImageIndex && !isModalOpen && (isPhone ? isVisible : isHovered)) {
                    v.muted = !isPhone && isHovered ? false : isVideoMuted;
                    v.play().catch(() => { });
                } else {
                    v.pause();
                    try { v.currentTime = 0; } catch (e) { }
                }
            } catch (e) { /* ignore */ }
        });

        audios.forEach((a, idx) => {
            try {
                if (activeAudioPost?.id === post.id || isSelected) {
                    a.pause();
                    return;
                }
                const shouldPlay = isPhone
                    ? false
                    : (idx === currentImageIndex && !isModalOpen && isHovered);
                if (shouldPlay) {
                    a.muted = isMuted;
                    a.play().catch(() => { });
                } else {
                    a.pause();
                    try { a.currentTime = 0; } catch (e) { }
                }
            } catch (e) { /* ignore */ }
        });

        return () => {
            videos.forEach(v => { try { v.pause(); } catch (e) { } });
            audios.forEach(a => { try { a.pause(); } catch (e) { } });
        };
    }, [currentImageIndex, isHovered, isVisible, isModalOpen, isPhone, isVideoMuted, isMuted, isSelected]);

    // Audio progress effect
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handleTimeUpdate = () => {
            setAudioCurrentTime(audio.currentTime);
        };

        const handleLoadedMetadata = () => {
            if (!isNaN(audio.duration)) {
                setAudioDuration(audio.duration);
            }
        };

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        if (!isNaN(audio.duration)) setAudioDuration(audio.duration);

        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [post.type, isVisible]);

    // Carousel auto-scroll effect
    useEffect(() => {
        if (!isGallery || isHovered || isDragging) return;

        const mediaItems = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.media : post.content.media;
        const mediaLength = mediaItems?.length || 1;
        if (mediaLength <= 1) return;

        const currentMedia = mediaItems?.[currentImageIndex];
        if (currentMedia?.type === 'video') return;

        const timeout = setTimeout(() => {
            setCurrentImageIndex(prev => (prev + 1) % mediaLength);
        }, 7000);

        return () => clearTimeout(timeout);
    }, [isGallery, isHovered, isDragging, post.type, post.content.media, post.originalPost?.content.media, currentImageIndex]);

    const handlePostClickWithMediaStop = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        markAsEncountered();
        if (post.isLive || (post as any).type === 'live_stream') {
            onNavigateToCommunity?.(post.communityId!, (post as any).liveStream?.channelId);
            return;
        }
        if (videoRef.current) { videoRef.current.pause(); videoRef.current.currentTime = 0; videoRef.current.muted = true; }
        setIsHovered(false);
        setIsVisible(false);
        setIsWheelVisible(false);
        const targetPost = (isSimpleBump && post.originalPost) ? post.originalPost : post;
        setTimeout(() => onPostClick(targetPost), 200);
    }, [onPostClick, post, isSimpleBump, markAsEncountered]);

    const activeStyleAuthor = post.type === 'bump' ? (post.originalPost?.author || post.author) : post.author;
    let finalPostcardStyle = appearanceSource.postcardStyle || usersData[activeStyleAuthor]?.activeCustomizations?.postcardStyle || 'classic_postcards';

    if (viewerPostcardAppearance === 'equipped' && viewerPostcardDefault) {
        finalPostcardStyle = viewerPostcardDefault;
    } else if (viewerPostcardAppearance === 'original') {
        finalPostcardStyle = post.postcardStyle || 'classic_postcards';
    }

    if (finalPostcardStyle === 'postcard_classic') finalPostcardStyle = 'classic_postcards';

    const postType = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.type : post.type;
    const originalText = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.text : post.content.text;
    const bumpCaption = (post.type === 'bump' || post.type === 'repost') ? post.content.text : undefined;
    // Use whichever text is longer to determine the shape
    const textContent = (bumpCaption?.length || 0) > (originalText?.length || 0) ? bumpCaption : originalText;
    const normalizedShape = useMemo(() => {
        const linkPreviewObj = (post.type === 'bump' || post.type === 'repost') ? post.originalPost?.content.linkPreview : post.content.linkPreview;
        const mediaObj = firstMedia || (linkPreviewObj ? { ...linkPreviewObj } : undefined);
        return getNormalizedPostcardShape(post.postcardShape, postType, mediaObj, textContent, post.textSize, columns);
    }, [post.postcardShape, postType, firstMedia, post.type, post.originalPost?.content.linkPreview, post.content.linkPreview, textContent, post.textSize, columns]);

    let shouldShowCaption = post.showCaption !== false;
    if (columns === 3 && postType !== 'text') {
        shouldShowCaption = false;
    }

    const containerClasses = useMemo(() => {
        const base = `post-card flex flex-col transition-transform ${isPiP ? '!transform-none !static !overflow-visible' : (isOptionsOpen ? 'hover:scale-[1.02] relative overflow-visible z-[45]' : 'hover:scale-[1.02] relative overflow-hidden')} group ${isSelected ? 'postcard-selected' : ''}`;

        const isFull = finalPostcardStyle === 'postcard_full';

        // Apply class based on style
        // Full has no shadow/rounded corners. Sticky retains classic rounded corners but has no gaps (handled in spacingClass).
        let styleClass = isFull ? "rounded-none shadow-none" : "rounded-2xl shadow-lg";

        if (post.type === 'link') {
            styleClass = `bg-black ${isFull ? 'rounded-none shadow-none' : 'rounded-2xl shadow-lg'}`;
        }

        return `${base} ${styleClass}`;
    }, [finalPostcardStyle, post.type, isPiP, isSelected, isOptionsOpen]);

    // Stickerized outline style when selected
    const selectedOutlineStyle = useMemo((): React.CSSProperties => {
        if (!isSelected) return {};
        // Use the viewer's own app theme color, not the post's color
        const currentUserData = usersData[currentUser];
        let glowHex = currentUserData?.appThemeColor || '#3b82f6';
        // Parse hex → rgb for rgba() usage
        let glowRgb = '59, 130, 246'; // fallback blue
        try {
            let h = glowHex.replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            const r = parseInt(h.substring(0, 2), 16);
            const g = parseInt(h.substring(2, 4), 16);
            const b = parseInt(h.substring(4, 6), 16);
            if (!isNaN(r) && !isNaN(g) && !isNaN(b)) glowRgb = `${r}, ${g}, ${b}`;
        } catch { }
        return {
            // Layered glow: thin solid ring + two expanding blur halos
            boxShadow: [
                `0 0 0 2px rgba(${glowRgb}, 0.9)`,
                `0 0 12px 4px rgba(${glowRgb}, 0.55)`,
                `0 0 28px 10px rgba(${glowRgb}, 0.25)`,
            ].join(', '),
            transition: 'box-shadow 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
            zIndex: 30,
        };
    }, [isSelected, usersData, currentUser]);

    const { sizeClasses, gridStyle } = useMemo(() => {

        const getBaseClasses = (span: number, start?: number, rowSpan: number = 2, vCenter: boolean = true) => {
            let classes = vCenter ? "self-center " : "";
            return {
                classes: classes.trim(),
                style: {
                    gridColumn: start ? `${start} / span ${span}` : `span ${span}`,
                    gridRow: `span ${rowSpan}`
                }
            };
        };

        // --- SINGLE COLUMN MODE ---
        if (columns === 1) {
            const style = { gridColumn: 'span 60 / span 60' };
            if (normalizedShape === 'square' || normalizedShape === 'wide_tall') return { sizeClasses: "aspect-square w-full", gridStyle: style };
            if (normalizedShape === 'wide') return { sizeClasses: "aspect-[2/1] w-full", gridStyle: style };
            if (normalizedShape === 'tall') return { sizeClasses: "aspect-[2/3] w-full", gridStyle: style };
            return { sizeClasses: "h-auto w-full", gridStyle: style };
        }

        const getGridConfig = (shape: string) => {
            if (columns === 6) {
                if (shape === 'wide') return getBaseClasses(20, undefined, 2);
                if (shape === 'wide_tall') return getBaseClasses(20, undefined, 4);
                if (shape === 'tall') return getBaseClasses(10, undefined, 3);
                return getBaseClasses(10, undefined, 2);
            }
            if (columns === 5) {
                if (shape === 'wide') return getBaseClasses(24, undefined, 2);
                if (shape === 'wide_tall') return getBaseClasses(24, undefined, 4);
                if (shape === 'tall') return getBaseClasses(12, undefined, 3);
                return getBaseClasses(12, undefined, 2);
            }
            if (columns === 4) {
                if (shape === 'wide') return getBaseClasses(30, undefined, 2);
                if (shape === 'wide_tall') return getBaseClasses(30, undefined, 4);
                if (shape === 'tall') return getBaseClasses(15, undefined, 3);
                return getBaseClasses(15, undefined, 2);
            }
            if (columns === 3) {
                if (shape === 'wide') return getBaseClasses(40, undefined, 2);
                if (shape === 'wide_tall') return getBaseClasses(40, undefined, 4);
                if (shape === 'tall') return getBaseClasses(20, undefined, 3);
                return getBaseClasses(20, undefined, 2);
            }
            if (columns === 2) {
                if (shape === 'wide') return getBaseClasses(60, undefined, 2);
                if (shape === 'wide_tall') return getBaseClasses(60, undefined, 4);
                if (shape === 'tall') return getBaseClasses(30, undefined, 3);
                return getBaseClasses(30, undefined, 2);
            }
            return getBaseClasses(60);
        };

        const config = getGridConfig(normalizedShape);
        let finalClasses = config.classes;
        let finalStyle = config.style;

        // Apply visual classes with strict aspect ratios to prevent stretching
        if (normalizedShape === 'square') {
            finalClasses += " aspect-square w-full";
        } else if (normalizedShape === 'wide_tall') {
            // Wide Tall is 2 columns wide and 4 rows tall.
            // 2 columns width / 4 rows height = 1:1 ratio.
            finalClasses += " aspect-square w-full";
        } else if (normalizedShape === 'wide') {
            // Wide is 2 columns wide and 2 rows tall.
            // 2 columns width / 2 rows height = 2:1 ratio.
            finalClasses += " aspect-[2/1] w-full";
        } else if (normalizedShape === 'tall') {
            // Tall is 1 column wide and 3 rows tall.
            // 3 rows height = 1.5 columns width.
            // Aspect ratio = 1 column / 1.5 columns = 2/3.
            finalClasses += " aspect-[2/3] w-full";
        } else {
            finalClasses += " h-full w-full";
        }

        return { sizeClasses: finalClasses, gridStyle: finalStyle };
    }, [columns, post.postcardShape, post.type, post.originalPost, post.content.media, post.content.linkPreview?.domain, normalizedShape]);

    const renderContent = (postType: PostType, recurs: boolean): React.ReactNode => {
        const textVal = recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '');
        const textLength = textVal.trim().length;

        // Auto-scale font size based on text length ONLY in 3-column mode to prevent text truncation with ellipses
        let currentTextSize = recurs ? (post.textSize || post.originalPost?.textSize) : post.textSize;
        const currentTextAlign = recurs ? (post.textAlign || post.originalPost?.textAlign || 'center') : (post.textAlign || 'center');
        
        if (!currentTextSize) {
            if (columns === 3) {
                if (normalizedShape === 'wide_tall') {
                    if (textLength > 450) {
                        currentTextSize = 'xs';
                    } else if (textLength > 220) {
                        currentTextSize = 'sm';
                    } else if (textLength > 100) {
                        currentTextSize = 'med';
                    } else {
                        currentTextSize = 'lg';
                    }
                } else {
                    if (textLength > 300) {
                        currentTextSize = 'xs';
                    } else if (textLength > 120) {
                        currentTextSize = 'sm';
                    } else {
                        currentTextSize = 'med';
                    }
                }
            } else {
                currentTextSize = recurs ? (post.textSize || post.originalPost?.textSize || 'med') : (post.textSize || 'med');
            }
        }

        const textSizes = {
            xs: 'text-xs leading-tight',
            sm: 'text-base leading-snug',
            med: 'text-xl font-medium',
            lg: 'text-2xl font-bold'
        };

        const currentTextSizeClass = textSizes[currentTextSize as keyof typeof textSizes] || textSizes.med;

        switch (postType) {
            case 'live_stream': {
                const streamObj = (post as any).liveStream?.stream;
                return <PostCardLiveStreamPlayer streamObj={streamObj} />;
            }
            case 'repost':
            case 'bump': {
                const isOriginalArchived = post.originalPost?.isArchived === true;
                const isOriginalUnavailable = !post.originalPost || isOriginalArchived;

                if (isOriginalUnavailable) {
                    return (
                        <div className="flex flex-col h-full group/bump">
                            <div className="p-3 absolute top-0 left-0 right-0 flex items-center gap-2 text-xs text-[#10b981] mb-2 z-50 text-raised font-bold pointer-events-none">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onNavigateToProfile(post.author); }}
                                    className="flex-shrink-0 rounded-full focus:outline-none relative"
                                >
                                    <img src={usersData[post.author]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(post.author) + '&backgroundColor=b6e3f4'} alt={post.author} className="w-6 h-6 rounded-full object-cover" loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Bump header PFP failed to load:', usersData[post.author]?.pfp); e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(post.author) + '&backgroundColor=b6e3f4'; }} />
                                </button>
                                {(columns === 1 || !isPhone) && (
                                    <span className="truncate text-green-400 font-bold">{(post.author === 'goodlyfe' || post.author === 'system') ? 'GoodieBoy' : (usersData[post.author]?.displayName || post.author)} bumped</span>
                                )}
                            </div>
                            <div className="flex flex-col h-full items-center justify-center p-6 bg-gray-900/40">
                                <GoodieWhiteIcon className="w-12 h-12 mb-2 opacity-50" />
                                <span className="text-[10px] text-white/40 uppercase tracking-widest font-black text-center px-4">Original post unavailable</span>
                                {shouldShowCaption && post.content.text && post.content.text.trim() !== '' && (
                                    <div className="mt-4 max-h-full overflow-hidden w-full text-center text-raised">
                                        <FormattedText text={post.content.text || ''} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                }
                return (
                    <div className="flex flex-col w-full h-full group/bump relative">
                        <div className="relative w-full h-full">
                            {renderContent(post.originalPost!.type, true)}
                        </div>
                    </div>
                );
            }
            case 'image':
            case 'video': {
                if (!mediaItems || mediaItems.length === 0) return null;

                if (isGallery) {
                    const currentItem = mediaItems[currentImageIndex];
                    const isCurrentItemVideo = currentItem?.type === 'video';
                    return (
                        <div className="relative w-full h-full overflow-hidden">
                            <div className="flex h-full" style={{ transform: `translateX(calc(-${currentImageIndex * 100}% + ${dragOffset}px))`, transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)' }}>
                                {mediaItems.map((m: MediaAttachment, i: number) => (
                                    <div key={`${m.url}-${i}`} className="relative w-full h-full flex-shrink-0 flex items-center justify-center overflow-hidden">
                                        {m.type === 'video' ? (
                                            isVisible ? (
                                                <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden">
                                                    {post.content.coverImageUrl && (
                                                        <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
                                                    )}
                                                    <video ref={i === currentImageIndex ? videoRef : null} src={`${m.url}${m.url.includes('?') ? '&' : '?'}retry=${reloadKeys[m.url] || 0}#t=0.001`} poster={post.content.coverImageUrl || undefined} className={`relative z-10 bg-transparent ${m.fit === 'horizontal' ? "w-full h-auto" : m.fit === 'vertical' ? "h-full w-auto" : "object-cover w-full h-full"}`} loop={mediaItems.length <= 1} muted={!isPhone && isHovered ? false : isVideoMuted} playsInline preload="metadata" onLoadedData={handleMediaLoad} onError={(e) => { console.error('[PostCard] Gallery video failed to load:', m.url); if (!autoReloadMedia(m.url)) { setMediaErrors(prev => ({ ...prev, [m.url]: true })); } handleMediaLoad(); }} onPlay={() => { setIsVideoPlaying(true); triggerMuteIconDisplay(); }} onPause={() => setIsVideoPlaying(false)} onEnded={() => { if (mediaItems.length > 1) { setCurrentImageIndex(prev => (prev + 1) % mediaItems.length); } }} />
                                                </div>
                                            ) : (
                                                <div className="w-full h-full bg-black/20 flex items-center justify-center relative">
                                                    {post.content.coverImageUrl && (
                                                        <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                                                    )}
                                                    <PlayIcon className="w-16 h-16 text-white/50 z-10 animate-pulse" />
                                                </div>
                                            )
                                        ) : (
                                            isVisible ? (
                                                <img src={`${m.url}${m.url.includes('?') ? '&' : '?'}retry=${reloadKeys[m.url] || 0}`} className={m.fit === 'horizontal' ? "w-full h-auto" : m.fit === 'vertical' ? "h-full w-auto" : "object-cover w-full h-full"} draggable={false} loading="lazy" decoding="async" onLoad={handleMediaLoad} onError={(e) => { console.error('[PostCard] Gallery image failed to load:', m.url); if (!autoReloadMedia(m.url)) { setMediaErrors(prev => ({ ...prev, [m.url]: true })); } handleMediaLoad(); }} />
                                            ) : (
                                                <div className="w-full h-full bg-black/10 flex items-center justify-center relative">
                                                    {post.content.coverImageUrl && (
                                                        <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-25 blur-md pointer-events-none" loading="lazy" />
                                                    )}
                                                </div>
                                            )
                                        )}
                                        {mediaErrors[m.url] && (
                                            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-30 pointer-events-auto">
                                                <span className="text-white text-xs font-bold mb-2 drop-shadow-md">Media failed to load</span>
                                                <button onClick={(e) => handleMediaReload(e, m.url)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white font-bold backdrop-blur-sm transition-all drop-shadow-md cursor-pointer">Reload</button>
                                            </div>
                                        )}
                                        {m.isLive && (
                                            <div className={`absolute top-2 right-2 z-20 pointer-events-none scale-75 origin-top-right transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                                <img src="/assets/goodieboy.png" alt="GoodLyfe Live" className="w-15 h-16" />
                                            </div>
                                        )}
                                        {m.isAIGenerated && (
                                            <div className={`absolute top-2 left-2 z-20 pointer-events-none scale-75 origin-top-left transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                                <img src="/assets/goodie_ai_sticker.png" alt="AI Generated" className="w-15 h-16 drop-shadow-md" />
                                            </div>
                                        )}
                                        {isCurrentItemVideo && i === currentImageIndex && (
                                            <>
                                                <div className={`absolute inset-0 bg-black/10 flex items-center justify-center transition-opacity pointer-events-none ${isVideoPlaying ? 'opacity-0' : 'opacity-50 group-hover:opacity-0'}`}><PlayIcon className="w-16 h-16 text-white/80" /></div>
                                                <div className={`absolute bottom-2 right-2 transition-opacity duration-300 z-20 ${showMuteIcon || !isVideoPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                                    <button onClick={toggleVideoMute} className="p-1.5 rounded-full text-white hover:bg-black/20">{isVideoMuted ? <VolumeOffIcon className="w-7 h-7" /> : <VolumeHighIcon className="w-7 h-7" />}</button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {shouldShowCaption && (post.content.text || (recurs && post.originalPost?.content.text)) && (
                                <div className={`absolute inset-0 p-4 flex flex-col items-center justify-center pointer-events-none z-10 transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-100' : 'opacity-0') : 'opacity-0 group-hover:opacity-100'}`}>
                                    <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-lg">
                                        <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                    </div>
                                </div>
                            )}

                            {/* Desktop Navigation Arrows */}
                            {!isPhone && (
                                <>
                                    <button
                                        onClick={prevSlide}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/50 z-20"
                                        aria-label="Previous slide"
                                    >
                                        <ChevronLeftIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={nextSlide}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/50 z-20"
                                        aria-label="Next slide"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </>
                            )}

                            {/* Carousel Dots */}
                            {mediaItems.length > 1 && (
                                <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-20 pointer-events-none">
                                    {mediaItems.map((_: any, idx: number) => (
                                        <div
                                            key={idx}
                                            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${idx === currentImageIndex ? 'bg-white scale-125 shadow-sm' : 'bg-white/40'}`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }

                const firstMedia = mediaItems[0];
                if (postType === 'video') {
                    if (!firstMedia) return null;
                    return (
                        <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                            {!isModalOpen && (
                                <>
                                    {isVisible ? (
                                        <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden">
                                            {post.content.coverImageUrl && (
                                                <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-50 pointer-events-none" />
                                            )}
                                            <video ref={videoRef} src={`${firstMedia?.url}${firstMedia?.url.includes('?') ? '&' : '?'}retry=${reloadKeys[firstMedia?.url || ''] || 0}#t=0.001`} poster={post.content.coverImageUrl || undefined} className={`relative z-10 bg-transparent ${firstMedia?.fit === 'horizontal' ? "w-full h-auto" : firstMedia?.fit === 'vertical' ? "h-full w-auto" : "object-cover w-full h-full"}`} loop muted={!isPhone && isHovered ? false : isVideoMuted} playsInline preload="metadata" onLoadedData={handleMediaLoad} onError={(e) => { console.error('[PostCard] Single video failed to load:', firstMedia?.url); if (firstMedia?.url) { if (!autoReloadMedia(firstMedia.url)) { setMediaErrors(prev => ({ ...prev, [firstMedia.url]: true })); } } handleMediaLoad(); }} onPlay={() => { setIsVideoPlaying(true); triggerMuteIconDisplay(); }} onPause={() => setIsVideoPlaying(false)} />
                                        </div>
                                    ) : (
                                        <div className="w-full h-full bg-black/20 flex items-center justify-center relative">
                                            {post.content.coverImageUrl && (
                                                <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-50" />
                                            )}
                                            <PlayIcon className="w-16 h-16 text-white/50 z-10 animate-pulse" />
                                        </div>
                                    )}
                                    {firstMedia?.url && mediaErrors[firstMedia.url] && (
                                        <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-30 pointer-events-auto">
                                            <span className="text-white text-xs font-bold mb-2 drop-shadow-md">Media failed to load</span>
                                            <button onClick={(e) => handleMediaReload(e, firstMedia.url)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white font-bold backdrop-blur-sm transition-all drop-shadow-md cursor-pointer">Reload</button>
                                        </div>
                                    )}
                                </>
                            )}
                            {firstMedia?.isLive && (
                                <div className={`absolute top-2 right-2 z-20 pointer-events-none scale-75 origin-top-right transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                    <img src="/assets/goodieboy.png" alt="GoodLyfe Live" className="w-15 h-16" />
                                </div>
                            )}
                            {firstMedia?.isAIGenerated && (
                                <div className={`absolute top-2 left-2 z-20 pointer-events-none scale-75 origin-top-left transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                    <img src="/assets/goodie_ai_sticker.png" alt="AI Generated" className="w-15 h-16 drop-shadow-md" />
                                </div>
                            )}
                            <div className={`absolute inset-0 bg-black/10 flex items-center justify-center transition-opacity pointer-events-none ${isVideoPlaying ? 'opacity-0' : 'opacity-50 group-hover:opacity-0'}`}><PlayIcon className="w-16 h-16 text-white/80" /></div>
                            <div className={`absolute bottom-2 right-2 transition-opacity duration-300 z-20 ${showMuteIcon || !isVideoPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                                <button onClick={toggleVideoMute} className="p-1.5 rounded-full text-white hover:bg-black/20">{isVideoMuted ? <VolumeOffIcon className="w-7 h-7" /> : <VolumeHighIcon className="w-7 h-7" />}</button>
                            </div>
                            {shouldShowCaption && (post.content.text || (recurs && post.originalPost?.content.text)) && (
                                <div className={`absolute inset-0 p-4 flex flex-col items-center justify-center pointer-events-none z-10 transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-100' : 'opacity-0') : 'opacity-0 group-hover:opacity-100'}`}>
                                    <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-lg">
                                        <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
                return (
                    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
                        {firstMedia && (
                            <>
                                {isVisible ? (
                                    <img src={`${firstMedia.url}${firstMedia.url.includes('?') ? '&' : '?'}retry=${reloadKeys[firstMedia.url] || 0}`} className={firstMedia.fit === 'horizontal' ? "w-full h-auto animate-fade-in-image" : firstMedia.fit === 'vertical' ? "h-full w-auto animate-fade-in-image" : "object-cover w-full h-full animate-fade-in-image"} loading="lazy" decoding="async" onLoad={handleMediaLoad} onError={(e) => { console.error('[PostCard] Single image failed to load:', firstMedia.url); if (!autoReloadMedia(firstMedia.url)) { setMediaErrors(prev => ({ ...prev, [firstMedia.url]: true })); } handleMediaLoad(); }} />
                                ) : (
                                    <div className="w-full h-full bg-black/10 flex items-center justify-center relative">
                                        {post.content.coverImageUrl && (
                                            <img src={post.content.coverImageUrl} className="absolute inset-0 w-full h-full object-cover opacity-25 blur-md pointer-events-none" loading="lazy" />
                                        )}
                                    </div>
                                )}
                                {mediaErrors[firstMedia.url] && (
                                    <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center z-30 pointer-events-auto">
                                        <span className="text-white text-xs font-bold mb-2 drop-shadow-md">Media failed to load</span>
                                        <button onClick={(e) => handleMediaReload(e, firstMedia.url)} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-white font-bold backdrop-blur-sm transition-all drop-shadow-md cursor-pointer">Reload</button>
                                    </div>
                                )}
                            </>
                        )}
                        {firstMedia?.isLive && (
                            <div className={`absolute top-2 right-2 z-20 pointer-events-none scale-75 origin-top-right transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                <img src="/assets/goodieboy.png" alt="GoodLyfe Live" className="w-15 h-16" />
                            </div>
                        )}
                        {firstMedia?.isAIGenerated && (
                            <div className={`absolute top-2 left-2 z-20 pointer-events-none scale-75 origin-top-left transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-70' : 'opacity-0') : 'opacity-0 group-hover:opacity-70'}`}>
                                <img src="/assets/goodie_ai_sticker.png" alt="AI Generated" className="w-15 h-16 drop-shadow-md" />
                            </div>
                        )}
                        {shouldShowCaption && (post.content.text || (recurs && post.originalPost?.content.text)) && (
                            <div className={`absolute inset-0 p-4 flex flex-col items-center justify-center pointer-events-none transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-100' : 'opacity-0') : 'opacity-0 group-hover:opacity-100'}`}>
                                <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-lg">
                                    <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                </div>
                            </div>
                        )}
                    </div>
                );
            }
            case 'audio':
            case 'voice': {
                const media = recurs ? post.originalPost?.content.media : post.content.media;
                const firstAudio = media?.find(m => m.type === 'audio' || m.type === 'voice');
                const isGaplessStyle = finalPostcardStyle === 'postcard_full' || finalPostcardStyle === 'postcard_sticky';
                const hasCoverImage = recurs ? !!post.originalPost?.content.coverImageUrl : !!post.content.coverImageUrl;
                const coverImageFit = recurs ? post.originalPost?.content.coverImageFit : post.content.coverImageFit;
                return (
                    <div className={`${isGaplessStyle ? 'p-0' : 'p-4 sm:p-6'} flex flex-col items-center justify-center w-full h-full relative group/audio overflow-hidden`}>
                        {hasCoverImage && (
                            <img
                                src={recurs ? post.originalPost?.content.coverImageUrl : post.content.coverImageUrl}
                                className={
                                    coverImageFit === 'horizontal' ? "absolute inset-0 w-full h-auto m-auto z-0" :
                                        coverImageFit === 'vertical' ? "absolute inset-0 h-full w-auto m-auto z-0" :
                                            "absolute inset-0 w-full h-full object-cover z-0"
                                }
                                onLoad={handleMediaLoad}
                                onError={(e) => { console.error('[PostCard] Audio cover image failed to load:', recurs ? post.originalPost?.content.coverImageUrl : post.content.coverImageUrl); handleMediaLoad(); }}
                            />
                        )}
                        {firstAudio && !isModalOpen && isVisible && (
                            <audio ref={audioRef} src={firstAudio?.url} preload="metadata" loop playsInline style={{ position: 'fixed', left: '0px', top: '0px', width: '1px', height: '1px', pointerEvents: 'none', opacity: 0, zIndex: -9999 }} />
                        )}
                        <div className="absolute inset-0 bg-black/10 group-hover/audio:bg-black/20 transition-colors"></div>
                        <div className="relative z-10 flex flex-col items-center justify-center text-center w-full px-4">
                            {shouldShowCaption && !(isPhone && columns === 3) && (
                                <div className={`text-raised w-full drop-shadow-lg transition-opacity duration-300 ${isPhone && hasCoverImage ? (showCaptionMobile ? 'opacity-100' : 'opacity-0') : 'opacity-100'}`}>
                                    <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                </div>
                            )}
                        </div>
                        <div 
                            className="absolute bottom-2 right-2 p-1.5 z-20 pointer-events-none"
                            style={{
                                color: postTextColor,
                                filter: postColorRgb ? `drop-shadow(0 0 12px rgba(${postColorRgb}, 0.8))` : `drop-shadow(0 0 12px ${appearanceSource.themeColor || '#000000'})`
                            }}
                        >
                            {postType === 'audio' ? <AudioIcon className="w-7 h-7" /> : <MicIcon className="w-7 h-7" />}
                        </div>
                        {!recurs && showSeekbar && (
                            <PostCardAudioSeekBar
                                post={post}
                                audioCurrentTime={audioCurrentTime}
                                audioDuration={audioDuration}
                                handleSeek={handleSeek}
                                handleSliderPointerDown={handleSliderPointerDown}
                                triggerSeekbarVisibility={triggerSeekbarVisibility}
                            />
                        )}
                    </div>
                );
            }
            case 'text': {
                let maxLines = 6;
                if (columns === 3) {
                    if (normalizedShape === 'wide_tall') {
                        if (currentTextSize === 'xs') maxLines = 26;
                        else if (currentTextSize === 'sm') maxLines = 18;
                        else if (currentTextSize === 'lg') maxLines = 10;
                        else maxLines = 14;
                    } else {
                        if (currentTextSize === 'xs') maxLines = 12;
                        else if (currentTextSize === 'sm') maxLines = 8;
                        else maxLines = 6;
                    }
                } else {
                    maxLines = normalizedShape === 'wide_tall' ? 14 : 6;
                }

                return (
                    <div className="p-6 flex items-center justify-center h-full w-full overflow-hidden">
                        <div
                            className="text-center text-raised w-full"
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: maxLines,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere'
                            }}
                        >
                            {shouldShowCaption ? (
                                <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                            ) : (
                                <span className={`${currentTextSizeClass} italic`} style={{ color: postTextColor }}>Open to see...</span>
                            )}
                        </div>
                    </div>
                );
            }
            case 'link': {
                const content = recurs ? post.originalPost?.content : post.content;
                const linkPreview = content?.linkPreview;
                if (!linkPreview) return null;
                const domain = linkPreview.domain;
                const isYoutube = domain === 'youtube.com' || domain === 'youtu.be' || !!ytVideoId;
                const isSharedIframeActive = activeVideo?.id === post.id;
                const isPlayingInline = isYtPlaying && isSharedIframeActive && (!activeVideoRect || !activeVideoRect.isPiP);

                if (isYoutube && isSharedIframeActive && activeVideoRect?.isPiP) {
                    let maxLines = 6;
                    if (columns === 3) {
                        if (normalizedShape === 'wide_tall') {
                            if (currentTextSize === 'xs') maxLines = 26;
                            else if (currentTextSize === 'sm') maxLines = 18;
                            else if (currentTextSize === 'lg') maxLines = 10;
                            else maxLines = 14;
                        } else {
                            if (currentTextSize === 'xs') maxLines = 12;
                            else if (currentTextSize === 'sm') maxLines = 8;
                            else maxLines = 6;
                        }
                    } else {
                        maxLines = normalizedShape === 'wide_tall' ? 14 : 6;
                    }

                    return (
                        <div className="p-6 flex items-center justify-center h-full w-full overflow-hidden">
                            <div
                                className="text-center text-raised w-full"
                                style={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: maxLines,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {shouldShowCaption ? (
                                    <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                ) : (
                                    <span className={`${currentTextSizeClass} italic`} style={{ color: postTextColor }}>Open to see...</span>
                                )}
                            </div>
                        </div>
                    );
                }

                if ((isVisible || isSharedIframeActive) && (linkPreview.embedHtml || (isYoutube && ytVideoId))) {
                    const isTiktok = domain === 'tiktok.com';
                    const isInsta = domain === 'instagram.com';
                    if (isTiktok && resolvedTiktokVideoId) {
                        return (
                            <div className="relative w-full h-full overflow-hidden group/embed cursor-pointer flex items-center justify-center">
                                <TiktokEmbed videoId={resolvedTiktokVideoId} apiUrl={API_URL} />

                                {/* Frame overlay to capture card clicks and bubble them up */}
                                <div className="absolute inset-0 z-20 pointer-events-none">
                                    <div className="absolute top-0 left-0 right-0 h-[20%] pointer-events-auto" />
                                    <div className="absolute bottom-0 left-0 right-0 h-[20%] pointer-events-auto" />
                                    <div className="absolute top-[20%] bottom-[20%] left-0 w-[20%] pointer-events-auto" />
                                    <div className="absolute top-[20%] bottom-[20%] right-0 w-[20%] pointer-events-auto" />
                                </div>

                                {/* Pre-play caption overlay */}
                                {shouldShowCaption && !isTiktokPlaying && (recurs ? (post.content.text || post.originalPost?.content.text) : post.content.text)?.trim() && (
                                    <div className="absolute inset-0 z-10 p-4 flex flex-col items-center justify-center pointer-events-none bg-black/40">
                                        <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-xl">
                                            <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    const isFacebook = domain === 'facebook.com' || domain === 'fb.watch';
                    if (isInsta || isFacebook) {
                        return (
                            <div className="relative w-full h-full overflow-hidden group/embed cursor-pointer bg-black flex items-center justify-center">
                                {isInsta ? (
                                    <>
                                        <InstagramEmbed url={linkPreview.url || ''} />
                                        {/* Full frame overlay to capture all clicks and open internal modal */}
                                        <div className="absolute inset-0 z-20 pointer-events-auto" />
                                    </>
                                ) : (
                                    <FacebookEmbed url={linkPreview.url || ''} preview={linkPreview} />
                                )}
                            </div>
                        );
                    }

                    if (isTiktok || isInsta) {
                        return (
                            <div className="p-4 flex flex-col justify-between h-full relative cursor-pointer group/link overflow-hidden">
                                {linkPreview?.image ? (
                                    <div className="absolute inset-0">
                                        <img src={linkPreview.image} className="w-full h-full object-cover transition-transform duration-500 group-hover/link:scale-110" loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Link Preview Image failed to load:', linkPreview.image); e.currentTarget.style.display = 'none'; handleMediaLoad(); }} onLoad={handleMediaLoad} />
                                        <div className="absolute inset-0 bg-black/50 group-hover/link:bg-black/30 transition-colors duration-500"></div>
                                    </div>
                                ) : (
                                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900 to-black"></div>
                                )}
                                <div className="relative z-10 flex flex-col items-center justify-center text-center h-full">
                                    <div className="mb-4 text-white/90 group-hover/link:text-white transition-all transform group-hover/link:scale-110 drop-shadow-2xl">
                                        {isTiktok ? <svg className="w-14 h-14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.59-5.71-.29-2.63.85-5.21 2.87-6.9 1.56-1.35 3.59-2.11 5.65-2.11.08 0 .16.01.24.01v3.39c-.88-.22-1.85-.07-2.62.45-1.01.67-1.58 1.83-1.46 3.02.08 1.12.78 2.13 1.81 2.58 1.05.47 2.32.26 3.16-.54.67-.64 1.05-1.56 1.05-2.49h.01V.02z" /></svg> : <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 flex items-center justify-center shadow-lg"><PlayIcon className="w-8 h-8 text-white" /></div>}
                                    </div>
                                    {shouldShowCaption && (
                                        <div className={`absolute inset-0 p-4 flex flex-col items-center justify-center pointer-events-none transition-opacity duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-100' : 'opacity-0') : 'opacity-100'}`}>
                                            <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-xl">
                                                <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    // Generic embeds (Apple Music, Spotify, SoundCloud, etc.)
                    // Parse src + fixed height from embedHtml to render a real iframe with onLoad
                    const isYoutube = domain === 'youtube.com' || domain === 'youtu.be' || !!ytVideoId;
                    if (!isYoutube) {
                        const embedHtml = linkPreview.embedHtml || '';
                        const srcMatch = embedHtml.match(/src="([^"]+)"/);
                        const embedSrc = srcMatch ? srcMatch[1] : null;
                        const isAppleMusic = domain === 'music.apple.com';
                        const isSpotify = domain === 'spotify.com';

                        // For Apple Music: detect song vs album/playlist by URL structure.
                        // Songs have ?i= (track from album page) or /song/ in the path.
                        // Albums and playlists should fill the full card.
                        // For other embeds (Spotify, SoundCloud): fall back to height attribute.
                        let hasSmallFixedHeight = false;
                        let fixedHeight: number | null = null;

                        if (isAppleMusic) {
                            const postUrl = linkPreview.url || '';
                            const isSong = postUrl.includes('?i=') || postUrl.includes('/song/');
                            hasSmallFixedHeight = isSong;
                            fixedHeight = isSong ? 175 : null;
                        } else {
                            const heightMatch = embedHtml.match(/height="(\d+)"/);
                            fixedHeight = heightMatch ? parseInt(heightMatch[1], 10) : null;
                            hasSmallFixedHeight = fixedHeight !== null && fixedHeight <= 200;
                        }

                        const iframeStyle: React.CSSProperties = hasSmallFixedHeight
                            ? { opacity: isEmbedLoaded ? 1 : 0, transition: 'opacity 0.4s ease', background: 'transparent', width: '100%', height: `${fixedHeight}px`, border: 'none', flexShrink: 0 }
                            : { opacity: isEmbedLoaded ? 1 : 0, transition: 'opacity 0.4s ease', background: 'transparent', width: '100%', height: '100%', border: 'none' };

                        // Apple Music needs 3s for internal assets; Spotify/SoundCloud only need ~500ms
                        const embedLoadDelay = isAppleMusic ? 3000 : 500;

                        return (
                            <div
                                className="relative w-full overflow-hidden"
                                style={{ height: '100%', display: 'flex', alignItems: hasSmallFixedHeight ? 'center' : 'stretch', justifyContent: 'center' }}
                            >
                                {/* Pulsating placeholder — shown until iframe loads */}
                                {!isEmbedLoaded && (
                                    <div className="absolute inset-0 bg-inherit flex flex-col items-center justify-center gap-3 z-10">
                                        {isAppleMusic ? (
                                            <div className="animate-pulse flex flex-col items-center gap-3">
                                                {/* Apple Music logo */}
                                                <svg viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg" className="w-14 h-14">
                                                    <defs>
                                                        <linearGradient id="amGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                            <stop offset="0%" stopColor="#fc5c7d" />
                                                            <stop offset="100%" stopColor="#6a3093" />
                                                        </linearGradient>
                                                    </defs>
                                                    <rect width="56" height="56" rx="12" fill="url(#amGrad)" />
                                                    <path d="M38 17.5H22.5a1.5 1.5 0 00-1.5 1.5v14.07a4.5 4.5 0 101.5 3.43V26.5l14-3.11V31a4.5 4.5 0 101.5 3.43V19a1.5 1.5 0 00-1.5-1.5z" fill="white" />
                                                </svg>
                                                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#fc5c7d' }}>Apple Music</span>
                                            </div>
                                        ) : isSpotify ? (
                                            <div className="animate-pulse flex flex-col items-center gap-3">
                                                {/* Spotify logo */}
                                                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-12 h-12" fill="#1DB954">
                                                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
                                                </svg>
                                                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#1DB954' }}>Spotify</span>
                                            </div>
                                        ) : (
                                            <div className="animate-pulse w-12 h-12 rounded-full bg-gray-200" />
                                        )}
                                    </div>
                                )}
                                {/* Real iframe — invisible until loaded */}
                                {embedSrc ? (
                                    <iframe
                                        src={embedSrc ? embedSrc.replace('theme=0', '') : ''} // Remove theme=0 to allow transparency if supported
                                        style={{ ...iframeStyle, background: 'transparent' }}
                                        allowTransparency={true}
                                        allow="autoplay *; encrypted-media *; fullscreen *; clipboard-write"
                                        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                                        onLoad={() => {
                                            if (embedLoaderTimeoutRef.current) {
                                                clearTimeout(embedLoaderTimeoutRef.current);
                                            }
                                            // Apple Music needs 3s to finish rendering; Spotify/SoundCloud only need ~500ms
                                            embedLoaderTimeoutRef.current = setTimeout(() => {
                                                setIsEmbedLoaded(true);
                                            }, embedLoadDelay);
                                        }}
                                        title={domain}
                                    />
                                ) : (
                                    <div dangerouslySetInnerHTML={{ __html: linkPreview.embedHtml || '' }} className="w-full h-full" />
                                )}
                            </div>
                        );
                    }

                    // The root-level shared iframe in App.tsx owns playback.
                    // This div is just a tracked placeholder — the iframe floats above it.

                    return (
                        <div
                            ref={ytContainerRef}
                            className="relative w-full h-full bg-black flex items-center justify-center group/yt"
                        >
                            {/* Watch on YouTube button to bypass verification/bot screen */}
                            <a
                                href={`https://www.youtube.com/watch?v=${ytVideoId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute top-3 right-3 bg-black/70 hover:bg-black/90 text-white font-bold py-1.5 px-2.5 rounded-full transition-all text-[11px] flex items-center gap-1 z-30 shadow-md hover:scale-105 active:scale-95 no-underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ExternalLinkIcon className="w-3.5 h-3.5" />
                                <span>Watch on YouTube</span>
                            </a>
                            {/* Interact/Lock Toggle Button — shown only when playing inline */}
                            {isPlayingInline && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setInteractMode(!interactMode);
                                    }}
                                    className="absolute top-3 left-3 bg-black/70 hover:bg-black/90 text-white font-bold py-1.5 px-2.5 rounded-full transition-all text-[11px] flex items-center gap-1 z-30 shadow-md hover:scale-105 active:scale-95 border-none cursor-pointer"
                                >
                                    <span>{interactMode ? '🔒 Lock' : '🔓 Interact'}</span>
                                </button>
                            )}
                            {/* Shown only when the shared iframe hasn't taken over yet */}
                            {!isSharedIframeActive && (
                                <iframe
                                    className="absolute inset-0 w-full h-full border-none"
                                    src={`${API_URL}youtube-proxy?videoId=${ytVideoId}&origin=${encodeURIComponent(window.location.origin)}`}
                                    allow="autoplay; fullscreen"
                                    title="YouTube Video Player"
                                />
                            )}

                            {/* Caption overlay: follows standard desktop hover / mobile auto-fade rules, hides when playing inline, stays visible in PiP */}
                            {shouldShowCaption && (!isYtPlaying || activeVideoRect?.isPiP) && (recurs ? (post.content.text || post.originalPost?.content.text) : post.content.text)?.trim() && (
                                <div className={`absolute inset-0 z-10 p-4 flex flex-col items-center justify-center pointer-events-none bg-black/40 transition-opacity duration-300 ${isPhone ? ((showCaptionMobile || isSelected) ? 'opacity-100' : 'opacity-0') : 'opacity-0 group-hover/yt:opacity-100'}`}>
                                    <div className="max-h-full w-full overflow-hidden text-center text-raised drop-shadow-xl">
                                        <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
                return (
                    <div className="p-6 flex flex-col justify-between h-full relative cursor-pointer group/linkSimple overflow-hidden">
                        {linkPreview?.image ? (
                            <img src={linkPreview.image} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover/linkSimple:scale-110" onLoad={handleMediaLoad} onError={(e) => { console.error('[PostCard] Simple Link Preview Image failed to load:', linkPreview.image); handleMediaLoad(); }} />
                        ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-purple-900/40 flex items-center justify-center">
                                <ExternalLinkIcon className="w-16 h-16 text-white/10" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 group-hover/linkSimple:bg-black/20 transition-colors duration-500"></div>
                        <div className="relative z-10 flex flex-col items-center justify-center text-center h-full gap-4">
                            {shouldShowCaption && (
                                <div className="text-raised w-full drop-shadow-xl">
                                    <div className="text-xs font-black uppercase tracking-[0.2em] text-white/60 mb-2">{linkPreview?.domain}</div>
                                    <FormattedText text={recurs ? (post.content.text || post.originalPost?.content.text || '') : (post.content.text || '')} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} textSize={currentTextSize} textAlign={currentTextAlign} textColor={postTextColor} fontFamily={postFontFamily} stickerize />
                                </div>
                            )}
                            <button onClick={() => linkPreview?.url && window.open(linkPreview.url, '_blank')} className="bg-white/10 backdrop-blur-xl px-6 py-2.5 rounded-full border border-white/30 text-[10px] font-black uppercase tracking-widest hover:bg-white/20 hover:scale-105 active:scale-95 transition-all shadow-2xl">Open Link</button>
                        </div>
                    </div>
                );
            }
            default: return null;
        }
    };

    const dragBackgroundClass = useMemo(() => {
        if (!isDragging || !isPhone) return "";
        if (dragOffset > 20) return "bg-blue-600/30"; // Dragging right - Reply feedback
        if (dragOffset < -20) return "bg-purple-600/30"; // Dragging left - Share feedback
        return "";
    }, [isDragging, dragOffset, isPhone]);

    const isGaplessSpacing = finalPostcardStyle === 'postcard_full' || finalPostcardStyle === 'postcard_sticky';
    const paddingStyle = isGaplessSpacing ? '0px' : `${postcardGap / 2}px`;

    const isBump = post.type === 'bump' || post.type === 'repost';
    const mediaItems = isBump ? post.originalPost?.content.media : post.content.media;

    return (
        <div 
            className={`${(isShakingMode || !isEncountered) ? 'ios-shaking' : ''} w-full ${sizeClasses} flex post-card min-h-[100px] sm:min-h-0`} 
            style={{
                ...(externalGridStyle || gridStyle),
                padding: paddingStyle
            }}
        >
            <div
                ref={containerRef}
                className={`${containerClasses} ${dragBackgroundClass} w-full flex-grow`}
                onMouseEnter={() => {
                    if (post.type !== 'link') {
                        setIsHovered(true);
                        if (!isPhone) {
                            setIsSelected(true);
                            if (audioRef.current) audioRef.current.muted = false;
                            if (videoRef.current) videoRef.current.muted = false;
                        }
                    }
                }}
                onMouseLeave={() => {
                    if (post.type !== 'link') {
                        setIsHovered(false);
                        if (!isPhone) {
                            setIsSelected(false);
                            if (audioRef.current) {
                                audioRef.current.muted = true;
                                audioRef.current.pause();
                                audioRef.current.currentTime = 0;
                            }
                            if (videoRef.current) {
                                videoRef.current.muted = true;
                            }
                        }
                    }
                }}
                onTouchStart={isPhone ? handleTouchStart : undefined}
                onTouchMove={isPhone ? handleTouchMove : undefined}
                onTouchEnd={isPhone ? handleTouchEnd : undefined}
                onTouchCancel={isPhone ? handleTouchCancel : undefined}
                style={{
                    backgroundColor: postColorRgb ? `rgba(${postColorRgb}, ${activeOpacity / 100})` : (post.type !== 'link' ? 'var(--app-theme-bg)' : 'transparent'),
                    color: postTextColor,
                    ...(isPhone && { userSelect: 'none', WebkitUserSelect: 'none' } as React.CSSProperties),
                    ...selectedOutlineStyle,
                    transform: `scale(${1 - 0.4 * ((postcardGap - 8) / 40)})`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.15s ease-out'
                }}
            >
                {/* Double Tap Heart Overlay */}
                {showDoubleTapHeart && (
                    <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none animate-double-tap-heart">
                        <LikeIcon className="w-24 h-24 text-[#ff4554] drop-shadow-[0_0_50px_rgba(255,69,84,0.5)]" isLiked={true} />
                    </div>
                )}
                {showDoubleTapBrokenHeart && (
                    <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none animate-double-tap-heart">
                        <BrokenHeartIcon className="w-24 h-24 text-gray-400 drop-shadow-[0_0_50px_rgba(156,163,175,0.5)]" />
                    </div>
                )}
                {/* Double Tap Vouch Overlay */}
                {showDoubleTapVouch && (
                    <div className="absolute inset-0 flex items-center justify-center z-[100] pointer-events-none animate-double-tap-heart">
                        <CheckmarkIcon className="w-24 h-24 text-[#8b5cf6] drop-shadow-[0_0_50px_rgba(139,92,246,0.5)]" isVouched={true} />
                    </div>
                )}

                {/* Pending Approval Badge & Approve Button for Admin */}
                {post.isApproved === false && (
                    <div className="absolute top-2.5 right-2.5 z-40 flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-full bg-amber-500/80 backdrop-blur-md text-[10px] font-black uppercase text-white shadow-lg tracking-wider">
                            Pending Approval
                        </span>
                        {onApprovePost && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onApprovePost(post.id);
                                }}
                                className="px-3 py-1 rounded-full bg-green-600 hover:bg-green-500 text-[10px] font-black uppercase text-white shadow-lg tracking-wider active:scale-95 transition-all"
                            >
                                Approve
                            </button>
                        )}
                    </div>
                )}

                {/* Pinned Post Indicator Badge */}
                {usersData[initialPost.author]?.pinnedPosts?.includes(post.id) && (
                    <div className="absolute top-2.5 right-2.5 z-40 px-2 py-1 flex items-center gap-1 pointer-events-none">
                        <span className="text-[16px] font-black uppercase tracking-wider text-white" style={stickerTextStyle(0.18)}>📌</span>
                    </div>
                )}

                <div
                    className={`w-full h-full bg-inherit relative z-10 grid grid-cols-1 grid-rows-1 ${post.type !== 'link' ? 'cursor-pointer' : ''}`}
                    style={{
                        transform: `translateX(${dragOffset}px)`,
                        transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                    }}
                    onClick={(e) => {
                        if (isPhone) return; // Handled by handleTouchEnd on mobile to prevent double-toggle selection
                        handlePostClickWithMediaStop(e as any);
                    }}
                >
                    {/* Minimum height spacer (equal to 1 column width) using grid stacking */}
                    {(postType === 'text' || postType === 'link' || postType === 'audio' || postType === 'voice') && (
                        <div
                            className="w-full pointer-events-none invisible"
                            style={{
                                gridArea: '1 / 1',
                                // Wide is now 2 rows tall.
                                // Aspect ratio is 2:1.
                                paddingBottom: normalizedShape === 'wide' ? '50%' : '100%'
                            }}
                        />
                    )}
                    <div className="w-full h-full" style={{ gridArea: '1 / 1' }}>
                        {renderContent(post.type, false)}
                    </div>
                    {(initialPost.type === 'bump' || initialPost.type === 'repost') && (() => {
                        const reposter = initialPost.author;
                        const reposterData = usersData[reposter];
                        const reposterTextColor = reposterData?.appTextColor || '#ffffff';
                        const reposterThemeColor = reposterData?.appThemeColor || '#000000';
                        return (
                            <div className="absolute top-3 left-3 flex items-center gap-1 z-50">
                                <span className="sticker-outline" style={{ '--sticker-outline-color': reposterThemeColor } as React.CSSProperties}>
                                    <BumpIcon
                                        className="w-4 h-4 mt-0.5"
                                        isBumped={true}
                                        fill="currentColor"
                                        stroke="currentColor"
                                        style={{
                                            color: reposterTextColor
                                        }}
                                    />
                                </span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if ((window as any).__pfpLpFired) {
                                            (window as any).__pfpLpFired = false;
                                            return;
                                        }
                                        onNavigateToProfile(initialPost.author);
                                    }}
                                    onMouseEnter={() => {
                                        if (window.matchMedia('(pointer: fine)').matches) {
                                            onHoldProfile?.(initialPost.author);
                                        }
                                    }}
                                    onTouchStart={() => {
                                        (window as any).__pfpLpFired = false;
                                        const timer = setTimeout(() => {
                                            (window as any).__pfpLpFired = true;
                                            onHoldProfile?.(initialPost.author);
                                        }, 400);
                                        (window as any).__pfpTimer = timer;
                                    }}
                                    onTouchEnd={() => clearTimeout((window as any).__pfpTimer)}
                                    onTouchCancel={() => clearTimeout((window as any).__pfpTimer)}
                                    className="flex-shrink-0 rounded-full focus:outline-none transition-all hover:scale-110 active:scale-95"
                                >
                                    <img src={usersData[initialPost.author]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.author) + '&backgroundColor=b6e3f4'} alt={initialPost.author} className="w-6 h-6 rounded-full object-cover shadow-sm" loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Card header PFP failed to load:', usersData[initialPost.author]?.pfp); e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.author) + '&backgroundColor=b6e3f4'; }} />
                                </button>
                                {(columns === 1 || !isPhone) && (
                                    <span
                                        className="text-[10px] font-black uppercase tracking-wider pointer-events-none truncate max-w-[150px]"
                                        style={{ color: reposterTextColor, ...stickerTextStyle(0.35) }}
                                    >
                                        {(initialPost.author === 'goodlyfe' || initialPost.author === 'system') ? 'GoodieBoy' : (usersData[initialPost.author]?.displayName || initialPost.author)} BUMPED
                                    </span>
                                )}
                            </div>
                        );
                    })()}
                    {post.showOnProfile === false && currentUser === post.author && (
                        <div className="absolute top-3 right-3 p-1.5 rounded-full z-[60] flex items-center gap-1.5 px-2.5 border border-white/20 shadow-2xl scale-90 origin-top-right">
                            <EyeOffIcon className="w-3.5 h-3.5 text-blue-400" />
                            <span className="text-[9px] font-black uppercase tracking-widest text-white/90"></span>
                        </div>
                    )}

                    {post.isForSale && (
                        <div className="absolute top-3 right-3 z-[60] flex flex-col items-end gap-2">
                            {/* Price Tag Badge */}
                            <div className="px-3 py-1.5 rounded-full bg-green-500/90 backdrop-blur-md shadow-lg border border-white/20 flex items-center gap-1.5 transition-transform hover:scale-105">
                                <span className="text-[9px] font-black uppercase tracking-widest text-white/95">FOR SALE</span>
                                <span className="text-sm font-black text-white" style={stickerTextStyle(0.15)}>
                                    ${post.price?.toFixed(2) || '0.00'}
                                </span>
                            </div>

                            {/* Add to Cart Button */}
                            {onAddToCart && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAddToCart(post);
                                    }}
                                    className="flex items-center gap-1.5 bg-white text-black font-black text-xs px-3.5 py-2 rounded-full shadow-lg transition-transform active:scale-95 hover:scale-105 cursor-pointer"
                                    style={{ ...stickerTextStyle(0.18) }}
                                >
                                    <span>🛒</span>
                                    <span>Add to Cart</span>
                                </button>
                            )}
                        </div>
                    )}

                    {!isMediaLoaded && isVisible && (
                        <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-900/80 animate-pulse">
                            <div className="relative w-12 h-12">
                                <div className="absolute inset-0 border-4 border-blue-500/20 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        </div>
                    )}
                </div>

                {isPhone && !isGallery && (
                    <>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-8 pointer-events-none z-0">
                            <div style={{
                                transform: `scale(${Math.min(1.5, Math.abs(dragOffset) / 50)})`,
                                opacity: dragOffset < 0 ? 1 : 0,
                                transition: 'opacity 0.15s, transform 0.15s',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <SendIcon className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <div className="absolute inset-y-0 left-0 flex items-center pl-8 pointer-events-none z-0">
                            <div style={{
                                transform: `scale(${Math.min(1.5, Math.abs(dragOffset) / 50)})`,
                                opacity: dragOffset > 0 ? 1 : 0,
                                transition: 'opacity 0.15s, transform 0.15s',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <CommentIcon className="w-8 h-8 text-white" />
                            </div>
                        </div>
                    </>
                )}

                <div className="absolute bottom-2 left-2 z-20 flex flex-col items-start gap-1">
                    {(initialPost.type === 'bump' || initialPost.type === 'repost') && !isSimpleBump && (
                        <div className="flex items-center max-w-[90%] text-white text-xs gap-1.5">
                            {/* PFP: navigate to the displayed author's profile */}
                            {(!initialPost.originalPost || initialPost.originalPost.isArchived) ? (
                                <div className={(columns === 1 ? "w-12 h-12" : (columns === 3 ? "w-6 h-6" : "w-9 h-9")) + " rounded-full flex items-center justify-center flex-shrink-0 bg-black/50"}>
                                    <TrashIcon className={columns === 3 ? "w-3 h-3 text-red-400/50" : "w-4 h-4 text-red-400/50"} />
                                </div>
                            ) : (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const profileTarget = !showOriginal
                                            ? ('' + initialPost.originalPost?.author)
                                            : ('' + initialPost.author);
                                        onNavigateToProfile(profileTarget);
                                    }}
                                    className="flex-shrink-0 rounded-full focus:outline-none bg-transparent"
                                >
                                    {!showOriginal
                                        ? <img src={usersData['' + initialPost.originalPost?.author]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.originalPost?.author || '') + '&backgroundColor=b6e3f4'} alt={'' + initialPost.originalPost?.author} className={columns === 1 ? "w-12 h-12 rounded-full object-cover" : (columns === 3 ? "w-6 h-6 rounded-full object-cover" : "w-9 h-9 rounded-full object-cover")} loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Modal original author PFP failed to load:', usersData['' + initialPost.originalPost?.author]?.pfp); e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.originalPost?.author || '') + '&backgroundColor=b6e3f4'; }} />
                                        : <img src={usersData['' + initialPost.author]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.author || '') + '&backgroundColor=b6e3f4'} alt={'' + initialPost.author} className={columns === 1 ? "w-12 h-12 rounded-full object-cover" : (columns === 3 ? "w-6 h-6 rounded-full object-cover" : "w-9 h-9 rounded-full object-cover")} loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Modal author PFP failed to load:', usersData['' + initialPost.author]?.pfp); e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(initialPost.author || '') + '&backgroundColor=b6e3f4'; }} />
                                    }
                                </button>
                            )}
                            {/* Toggle button: switches between bump and original */}
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (initialPost.originalPost && !initialPost.originalPost.isArchived) {
                                        setShowOriginal(!showOriginal);
                                    }
                                }}
                                disabled={!initialPost.originalPost || initialPost.originalPost.isArchived}
                                className={`flex items-center pr-2 pl-2 py-1 rounded-full transition-all duration-300 ${(!initialPost.originalPost || initialPost.originalPost.isArchived) ? 'cursor-default' : 'hover:font-semibold cursor-pointer'} ${showCommunityBadge ? ((!initialPost.originalPost || initialPost.originalPost.isArchived) ? 'opacity-80 translate-x-0' : 'opacity-100 translate-x-0') : ((!initialPost.originalPost || initialPost.originalPost.isArchived) ? 'opacity-0 -translate-x-2 pointer-events-none group-hover:opacity-80 group-hover:translate-x-0 group-hover:pointer-events-auto' : 'opacity-0 -translate-x-2 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto')}`}
                                style={{ backgroundColor: toggleBgRgba }}
                            >
                                {(() => {
                                    const toggleText = (!initialPost.originalPost || initialPost.originalPost.isArchived)
                                        ? "Original post not found or deleted"
                                        : (showOriginal
                                            ? (initialPost.content.text || '')
                                            : (initialPost.originalPost.content.text || '')
                                        );

                                    return (
                                        <span
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                textAlign: 'center',
                                                wordBreak: 'normal',
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            {toggleText}
                                        </span>
                                    );
                                })()}
                            </button>
                        </div>
                    )}
                    {((initialPost.type !== 'bump' && initialPost.type !== 'repost') || isSimpleBump) && (
                        <div className="flex items-center gap-1.5 overflow-hidden">
                            <div className="flex flex-col items-center gap-1">
                                {/* Like/Comment Counts overlay above PFP with caption show/hide logic */}
                                <div
                                    className={`flex flex-col items-center gap-0.5 pointer-events-none transition-all duration-300 ${isPhone ? (showCaptionMobile ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2') : (isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2')
                                        }`}
                                >
                                    {(post.likes > 0 || post.comments?.length > 0 || (post.vouches?.length ?? 0) > 0) && (
                                        <div className="flex flex-col items-center gap-1 mb-1 bg-black/60 backdrop-blur-md rounded-full py-1 px-1.5 border border-white/10 shadow-lg scale-90">
                                            {post.likes > 0 && (
                                                <span className="text-[9px] font-black text-white flex items-center gap-0.5">
                                                    ❤️ {post.likes}
                                                </span>
                                            )}
                                            {post.comments?.length > 0 && (
                                                <span className="text-[9px] font-black text-white flex items-center gap-0.5">
                                                    💬 {post.comments.length}
                                                </span>
                                            )}
                                            {post.vouches && post.vouches.length > 0 && (
                                                <span className="text-[9px] font-black text-white flex items-center gap-0.5">
                                                    ✅ {post.vouches.length}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if ((window as any).__pfpLpFired) {
                                            (window as any).__pfpLpFired = false;
                                            return;
                                        }
                                        onNavigateToProfile(appearanceSource.author);
                                    }}
                                    onMouseEnter={() => {
                                        if (window.matchMedia('(pointer: fine)').matches) {
                                            onHoldProfile?.(appearanceSource.author);
                                        }
                                    }}
                                    onTouchStart={() => {
                                        (window as any).__pfpLpFired = false;
                                        const timer = setTimeout(() => {
                                            (window as any).__pfpLpFired = true;
                                            onHoldProfile?.(appearanceSource.author);
                                        }, 400);
                                        (window as any).__pfpTimer = timer;
                                    }}
                                    onTouchEnd={() => clearTimeout((window as any).__pfpTimer)}
                                    onTouchCancel={() => clearTimeout((window as any).__pfpTimer)}
                                    className="flex items-center gap-2 rounded-full text-white text-sm font-semibold bg-transparent hover:bg-black/5 transition-colors flex-shrink-0"
                                >
                                    <img src={usersData[appearanceSource.author]?.pfp || 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(appearanceSource.author) + '&backgroundColor=b6e3f4'} alt={appearanceSource.author} className={columns === 1 ? "w-12 h-12 rounded-full object-cover flex-shrink-0" : (columns === 3 ? "w-6 h-6 rounded-full object-cover flex-shrink-0" : "w-9 h-9 rounded-full object-cover flex-shrink-0")} loading="lazy" decoding="async" onError={(e) => { console.error('[PostCard] Footer PFP failed to load:', usersData[appearanceSource.author]?.pfp); e.currentTarget.src = 'https://api.dicebear.com/7.x/bottts/svg?seed=' + encodeURIComponent(appearanceSource.author) + '&backgroundColor=b6e3f4'; }} />
                                </button>
                            </div>
                            {initialPost.communityId && (() => {
                                const community = communities?.find((c: any) => c._id === initialPost.communityId || c.id === initialPost.communityId);
                                return (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onNavigateToCommunity?.(initialPost.communityId!); }}
                                        className={`flex items-center gap-1 bg-black/50 rounded-full py-1 px-2 text-[10px] font-bold text-white hover:bg-black/70 transition-all duration-300 border border-white/10 flex-shrink-0 min-w-0 ${!isPhone ? 'opacity-0 -translate-x-2 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto' : (showCommunityBadge || isSelected ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto')}`}
                                    >
                                        {community?.avatar ? (
                                            <img src={community.avatar} alt={community.name} className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                                        ) : (
                                            <UsersIcon className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                        )}
                                        <span className="truncate">{community?.name || initialPost.communityId}</span>
                                    </button>
                                );
                            })()}
                        </div>
                    )}
                    {getModBadgesForPost(post, usersData, initialPost.author).map((badge, idx) => {
                        const BadgeIcon = badge.icon;
                        const hexToRgba = (hex: string, alpha: number) => {
                            let c = hex.replace('#', '');
                            if (c.length === 3) c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
                            try {
                                const r = parseInt(c.substring(0, 2), 16);
                                const g = parseInt(c.substring(2, 4), 16);
                                const b = parseInt(c.substring(4, 6), 16);
                                return `rgba(${r}, ${g}, ${b}, ${alpha})`;
                            } catch (e) {
                                return `rgba(0, 0, 0, ${alpha})`;
                            }
                        };
                        return (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const ev = new CustomEvent(badge.onClickEvent, { detail: badge.onClickDetail });
                                    window.dispatchEvent(ev);
                                }}
                                className="flex items-center gap-1.5 rounded-full py-1 px-2 text-white max-w-[150px] transition-colors hover:brightness-110"
                                style={{ backgroundColor: hexToRgba(badge.themeColor, 0.5) }}
                                title={badge.title}
                            >
                                {typeof BadgeIcon === 'string' ? (
                                    <img src={BadgeIcon} className="w-4 h-4 flex-shrink-0 rounded-full object-cover" alt={badge.title} />
                                ) : BadgeIcon ? (
                                    React.createElement(BadgeIcon, { className: "w-4 h-4 flex-shrink-0" })
                                ) : badge.fallbackIconName === 'footprint' ? (
                                    <img src="/assets/footprint_logo.png" className="w-4 h-4 flex-shrink-0 rounded-full" alt="Footprint" />
                                ) : (
                                    <BellIcon className="w-4 h-4 flex-shrink-0" />
                                )}
                                <span className="text-xs font-medium truncate text-raised">{badge.title}</span>
                            </button>
                        );
                    })}
                    {/* Relocated Track Link Badge */}
                    {(() => {
                        const targetMedia = (initialPost.type === 'bump' || initialPost.type === 'repost') ? initialPost.originalPost?.content.media : initialPost.content.media;
                        const audioAttachment = targetMedia?.find((m: any) => m.type === 'audio' || m.type === 'voice');
                        if (!audioAttachment?.originalUrl) return null;
                        const isOriginal = initialPost.type === 'bump' || initialPost.type === 'repost';
                        const coverArtUrl = isOriginal ? initialPost.originalPost?.content.coverImageUrl : initialPost.content.coverImageUrl;
                        return (
                            <a
                                href={audioAttachment.originalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1.5 bg-black/50 border border-white/10 rounded-full py-1 px-2 text-white max-w-[150px] hover:bg-black/70 transition-all active:scale-95 duration-300"
                                title={audioAttachment.name || 'Open Track'}
                            >
                                {coverArtUrl ? (
                                    <img src={coverArtUrl} className="w-4 h-4 flex-shrink-0 rounded-md object-cover" alt="" />
                                ) : (
                                    <span className="text-xs">🎵</span>
                                )}
                                <span className="text-[10px] font-bold truncate text-raised uppercase tracking-wider">
                                    {audioAttachment.name || 'Open Track'}
                                </span>
                            </a>
                        );
                    })()}
                </div>

                {isNonMemberOfPublicCommunity ? (
                    <div className="flex items-center justify-center py-6 px-4">
                        <p className="text-sm text-gray-400 text-center">Join this community to interact with posts</p>
                    </div>
                ) : (
                    <>
                        {!isPhone && (
                            <PostActionButtonBar
                                post={post}
                                currentUser={currentUser}
                                usersData={usersData}
                                onDelete={(id) => isSimpleBump ? onDelete(initialPost.id) : onDelete(id)}
                                onEdit={onEdit}
                                onBookmark={onBookmark}
                                onBump={onBump}
                                onVouch={onVouch}
                                onShare={(p) => onShare && onShare(p, currentImageIndex)}
                                onLike={onLike}
                                onMuteUser={onMuteUser}
                                onBlockUser={onBlockUser}
                                onGrantAdmin={onGrantAdmin}
                                onDeleteUser={onDeleteUser}
                                onReportPost={onReportPost}
                                onArchive={onArchive}
                                onUnarchive={onUnarchive}
                                handlePostClickWithMediaStop={handlePostClickWithMediaStop}
                                animatingLikes={animatingLikes}
                                animatingBumps={animatingBumps}
                                animatingBookmarks={animatingBookmarks}
                                isDemo={isDemo}
                                isSimpleBump={isSimpleBump}
                                bumperAuthor={String(initialPost.author)}
                                onPin={onPin}
                                onUnpin={onUnpin}
                                currentImageIndex={currentImageIndex}
                                onToggleFit={handleToggleFit}
                                onToggleShape={handleToggleShape}
                                isCardHovered={isHovered}
                                onOptionsOpenChange={setIsOptionsOpen}
                            />
                        )}


                        {isPhone && isWheelVisible && (
                            <PostActionWheel
                                post={post}
                                currentUser={currentUser}
                                usersData={usersData}
                                onLike={onLike}
                                onBookmark={onBookmark}
                                onBump={onBump}
                                onShare={(p) => onShare && onShare(p, currentImageIndex)}
                                onEdit={onEdit}
                                onDelete={(id) => isSimpleBump ? onDelete(initialPost.id) : onDelete(id)}
                                onVouch={onVouch}
                                onMuteUser={onMuteUser}
                                onBlockUser={onBlockUser}
                                onReportPost={onReportPost}
                                onArchive={onArchive}
                                onUnarchive={onUnarchive}
                                handlePostClickWithMediaStop={handlePostClickWithMediaStop}
                                animatingLikes={animatingLikes}
                                animatingBumps={animatingBumps}
                                animatingBookmarks={animatingBookmarks}
                                postBlurOpacity={postBlurOpacity}
                                postColorRgb={postColorRgb}
                                columns={columns}
                                setIsWheelVisible={setIsWheelVisible}
                                handleCloseWheel={handleCloseWheel}
                                isDemo={isDemo}
                                isSimpleBump={isSimpleBump}
                                bumperAuthor={String(initialPost.author)}
                                onPin={onPin}
                                onUnpin={onUnpin}
                                currentImageIndex={currentImageIndex}
                                onToggleFit={handleToggleFit}
                                onToggleShape={handleToggleShape}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
});
